// @ts-nocheck
/**
 * Сцена доски: рендер, ops, sync.
 * Поведение указателя — в editor/ToolController + tools/*.
 */

import { createToolController, type ToolController } from "../../editor/ToolController";
import { createSelectionFrame } from "../../editor/selectionFrame";
import { createSegmentPointHandles } from "../../editor/segmentPointHandles";
import { sceneSvgDefs } from "../../editor/render/SvgRenderer.js";
import { isBoardToolId, type BoardToolId } from "../../editor/types";
import { normalizeScene as normalizeSceneShared } from "@bezpart-board/shared";
import { SceneStore } from "../../editor/store/SceneStore.js";
import { OpsQueue } from "../../editor/store/opsQueue.js";
import { applyLocalOps, applyLocalOpsLenient, cloneScene } from "../../editor/store/applyLocalOps.js";
import { undoEntryForAdd, undoEntryForAdds, undoEntryForDeletes, undoEntryForUpdates } from "../../editor/store/undoHelpers.js";
import { nextZIndex, bottomZIndex, computeZOrderPatches } from "../../editor/zIndex.js";
import { cloneSceneObjects } from "../../editor/objectClone.js";
import { sanitizeObjectsForOps } from "../../editor/sanitizeObjectForOps.js";
import { noteDiagAction, noteDiagOps } from "./boardDiagTrail.js";
import { installBoardSelectionGuard } from "../../editor/boardSelectionGuard.js";
import {
  delayMs,
  formatBoardOpsErrorToast,
  isAddOnlyOps,
  isRetryableLiveTextConflict,
  isRetryableRevisionConflict,
  isTransientOpsError,
  OPS_MAX_CONFLICT_ATTEMPTS,
  OPS_MAX_TRANSIENT_ATTEMPTS,
  opsRetryDelayMs,
} from "./boardOpsRetry.js";
import {
  expandSelectionToWholeGroups,
  objectGroupId,
  withGroupIdMeta,
} from "../../editor/grouping.js";
import {
  MAX_OBJECT_COMMENTS,
  normalizeCommentText,
  objectCommentCount,
  readObjectComments,
  withCommentsMeta,
} from "../../editor/objectComments.js";
import {
  hasInternalClipboard,
  preparePasteClones,
  setInternalClipboard,
  writeBoardClipboardToSystem,
  estimateTextObjectSize,
} from "../../editor/clipboard.js";
import { remapPasteAssets } from "../../editor/remapPasteAssets.js";
import { getTextSettings, textStyleFields } from "../../editor/textSession.js";
import { renderSvgSceneLayer, createSceneSvgLayer } from "../../editor/render/SvgRenderer.js";
import { cullSceneObjects, expandView } from "../../editor/render/sceneCulling.js";
import { noteSceneRender } from "./perfProbe.js";
import { visibleWorldBoundsFromViewport } from "../../editor/smartGuides.js";
import { syncTextLikeOnGroup } from "../../editor/render/renderers/textObject.js";
import { createConnectorAnchorHandles } from "../../editor/connectorAnchorHandles.js";
import {
  clearBindingsToObject,
  computeBindingPatches,
  resolveAndApplySegment,
} from "../../editor/bindings/resolveBindings.js";
import {
  applyDocumentContext,
  collectPdfAnnotationIds,
  computePdfMovePatches,
  canControlPdfDocument,
  documentContextAt,
} from "../../editor/document/documentContext.js";
import {
  collectFrameChildIds,
  collectFrames,
  computeFrameMovePatches,
  computeFrameReparentPatch,
  applyFrameContext,
  frameAtPoint,
} from "../../editor/frame/frameContext.js";
import { importPresentationToBoard } from "../../lib/pdf/importPresentationToBoard.js";
import { getPdfPagePreviewUrl } from "../../lib/pdf/pdfRenderPages.js";
import { importAudioToBoard } from "../../lib/audio/importAudioToBoard.js";
import { collectMediaAssetIds, markAssetsDeleted } from "./markAssetsDeleted.js";
import { buildAlignPatches, buildDistributePatches } from "../../editor/alignObjects.js";
import {
  applyCalculatorAction,
  calculatorStateToPatch,
  readCalculatorState,
  CALCULATOR_DEFAULT_STATE,
  CALCULATOR_VARIANT,
} from "./calculatorLogic.js";
import { CALCULATOR_DEFAULT_SIZE, GRAPH_UNITS_DEFAULT_SIZE } from "./createCalculatorObject.js";
import {
  applyCodeAction,
  codeStatePatch,
  readCodeState,
  CODE_DEFAULTS,
} from "./codeLogic.js";
import { CODE_DEFAULT_SIZE } from "./createCodeObject.js";
import {
  applySqlAction,
  sqlStateToPatch,
  readSqlState,
  SQL_DEFAULTS,
} from "./sqlLogic.js";
import { SQL_DEFAULT_SIZE } from "./createSqlObject.js";
import { TIMER_DEFAULT_SIZE } from "./createTimerObject.js";
import { imageInsertSize } from "./imageInsertSize.js";
import { isCodeWidgetTyping, clearCodeWidgetTyping } from "../../editor/render/renderers/code.js";
import { isSqlWidgetTyping, clearSqlWidgetTyping } from "../../editor/render/renderers/sql.js";
import { disposeTimersExcept, disposeAllTimers } from "../../editor/render/renderers/timer.js";

/** Маркер сборки — проверка в DevTools → Console после деплоя. */
export const EDITOR_CLIENT_BUILD = "2026-08-24-text-miro";

/**
 * Сервер принимает не больше 100 ops за запрос (apps/api boardOpsSchema).
 * 80 — запас на side-патчи (bindings/frame/pdf), которые добавляются к пакету.
 */
export const OPS_CHUNK_SIZE = 80;

function WbBoardScene(options) {
  this.stage = options.stage;
  this.boardId = options.boardId;
  this.shareToken = options.shareToken || null;
  this.guestEditKey = options.guestEditKey || null;
  this.useCredentials = Boolean(options.useCredentials);
  this.canEdit = Boolean(options.canEdit);
  this.store = new SceneStore({
    scene: normalizeScene(options.scene),
    revision: typeof options.revision === "number" ? options.revision : 0,
  });
  this._storeUnsub = null;
  this.layer = null;
  this.selectionOverlay = null;
  this.realtime = null;
  this.activeTool = isBoardToolId(options.toolMode || "") ? options.toolMode : "select";
  this.toolController = null;
  this.selectionFrameCtrl = null;
  this.segmentPointHandlesCtrl = null;
  this.connectorAnchorHandlesCtrl = null;
  this._textEditId = null;
  this._renderedSceneRef = null;
  this.onTextEditRequest =
    typeof options.onTextEditRequest === "function" ? options.onTextEditRequest : function () {};
  this.onSelectionChromeLayout =
    typeof options.onSelectionChromeLayout === "function"
      ? options.onSelectionChromeLayout
      : null;
  this.onRevision =
    typeof options.onRevision === "function" ? options.onRevision : function () {};
  this.onError =
    typeof options.onError === "function" ? options.onError : function () {};
  this.onSelectionChange =
    typeof options.onSelectionChange === "function"
      ? options.onSelectionChange
      : function () {};
  this.onHistoryChange =
    typeof options.onHistoryChange === "function" ? options.onHistoryChange : function () {};
  this.onActiveToolChange =
    typeof options.onActiveToolChange === "function"
      ? options.onActiveToolChange
      : function () {};
  /** Последняя позиция курсора на доске (клиентские координаты — куда вставлять Ctrl+V). */
  this._lastPointerClient = null;
  this._pastePointerHit = null;
  this._onPastePointerTrack = null;
  this.panBy = typeof options.panBy === "function" ? options.panBy : function () {};
  this.endPanGesture =
    typeof options.endPanGesture === "function" ? options.endPanGesture : function () {};
  this.mapClientToStage =
    typeof options.mapClientToStage === "function" ? options.mapClientToStage : null;
  this._opsQueue = new OpsQueue();
  /** add_object ids shown via ops_early from peers — держим до ops_applied или ops_revert. */
  this._earlyRemoteObjectIds = Object.create(null);
  /** Свои ops в полёте — переприменяются поверх серверных снапшотов. */
  this._pendingOps = [];
  this._culledView = null;
  this._cullRaf = 0;
  this._onCameraMoved = null;
}

function stagePoint(self, clientX, clientY) {
  if (self.mapClientToStage) {
    return self.mapClientToStage(clientX, clientY);
  }
  var viewportEl = self.stage.closest(".wb-board-viewport");
  var rect = viewportEl
    ? viewportEl.getBoundingClientRect()
    : self.stage.getBoundingClientRect();
  var sx = clientX - rect.left;
  var sy = clientY - rect.top;
  return { x: sx, y: sy };
}

function normalizeScene(raw) {
  return normalizeSceneShared(raw);
}

function parseFetchJson(res) {
  return res.text().then(function (text) {
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (_err) {
      if (!res.ok) {
        throw new Error("Request failed (" + res.status + ")");
      }
      throw new Error("Invalid server response");
    }
  });
}

WbBoardScene.prototype.getActiveTool = function () {
  return this.toolController
    ? this.toolController.getActiveTool()
    : this.activeTool;
};

WbBoardScene.prototype.isInteractionBlocked = function () {
  return Boolean(this._textEditId);
};

WbBoardScene.prototype.isTextEditing = function () {
  return Boolean(this._textEditId);
};

WbBoardScene.prototype.setTextEditing = function (id) {
  var prev = this._textEditId;
  this._textEditId = id || null;
  if (prev && prev !== id) {
    this.render();
  }
  if (!id) {
    this.render();
    return;
  }
  var el = this.getObjectElement(id);
  var obj = this.findObject(id);
  if (el && obj) {
    if (obj.type === "frame") {
      // Рамка осталась вектором: её название прячем прямо в SVG.
      var titleEl = el.querySelector(".wb-frame-title");
      if (titleEl) titleEl.setAttribute("visibility", "hidden");
    } else if (el instanceof HTMLElement) {
      syncTextLikeOnGroup(el, obj, true);
    }
  }
};

WbBoardScene.prototype.requestTextEdit = function (id) {
  if (!this.canEdit || !id) return;
  this.onTextEditRequest(id);
};

WbBoardScene.prototype.setActiveTool = function (toolId, force) {
  if (!isBoardToolId(toolId)) return;
  this.activeTool = toolId;
  if (this.toolController) {
    if (force) {
      this.toolController.remountTool(toolId);
    } else {
      this.toolController.setActiveTool(toolId);
    }
  }
  this.refreshSelectionChrome();
  if (typeof this.onActiveToolChange === "function") {
    this.onActiveToolChange(toolId);
  }
};

/** После создания объекта — «Курсор» (выделение и перенос). Ручка — исключение (серия штрихов). */
WbBoardScene.prototype._switchToSelectAfterObjectAdd = function () {
  if (!this.canEdit) return;
  if (this.getActiveTool() === "pen") return;
  // force: remount select + синхрон UI (toolbar), даже если tool уже select
  this.setActiveTool("select", true);
};

/** @deprecated используйте setActiveTool */
WbBoardScene.prototype.setToolMode = function (mode) {
  this.setActiveTool(mode);
};

WbBoardScene.prototype.setMapClientToStage = function (fn) {
  this.mapClientToStage = typeof fn === "function" ? fn : null;
};

WbBoardScene.prototype.getSelection = function () {
  return this.store.getSelection();
};

WbBoardScene.prototype.getObjects = function () {
  return this.store.getScene().objects.slice();
};

WbBoardScene.prototype.setSelection = function (ids) {
  var next = Array.isArray(ids) ? ids.filter(Boolean) : [];
  // Viewer: выделение не нужно и только путает (рамки / «как будто можно двигать»).
  if (!this.canEdit) next = [];
  next = expandSelectionToWholeGroups(next, this.getObjects());
  var cur = this.store.getSelection();
  var changed =
    next.length !== cur.length ||
    next.some(function (id, i) {
      return id !== cur[i];
    });
  if (!changed) return;
  this.store.setSelection(next);
};

WbBoardScene.prototype.groupSelection = function () {
  if (!this.canEdit) return Promise.resolve();
  var ids = this.getSelection();
  if (!ids || ids.length < 2) return Promise.resolve();
  var selectedSet = new Set(ids);
  var objects = this.getObjects();
  var selected = objects.filter(function (o) {
    return selectedSet.has(o.id) && !o.locked;
  });
  if (selected.length < 2) return Promise.resolve();

  var currentGroups = new Set();
  selected.forEach(function (o) {
    var gid = objectGroupId(o);
    if (gid) currentGroups.add(gid);
  });
  if (currentGroups.size === 1 && selected.every(function (o) { return objectGroupId(o); })) {
    return Promise.resolve();
  }

  var newGroupId = crypto.randomUUID();
  var patches = selected.map(function (o) {
    return { id: o.id, patch: { meta: withGroupIdMeta(o, newGroupId) } };
  });
  if (!patches.length) return Promise.resolve();
  var undoEntry = undoEntryForUpdates(this.store.getScene(), patches);
  var self = this;
  return this.commitBatchPatches(patches, "group-" + Date.now(), undoEntry).then(function () {
    self.setSelection(ids);
  });
};

WbBoardScene.prototype.ungroupSelection = function () {
  if (!this.canEdit) return Promise.resolve();
  var ids = this.getSelection();
  if (!ids || !ids.length) return Promise.resolve();
  var selectedSet = new Set(ids);
  var objects = this.getObjects();
  var selected = objects.filter(function (o) {
    return selectedSet.has(o.id) && !o.locked;
  });
  if (!selected.length) return Promise.resolve();

  var groupIds = new Set();
  selected.forEach(function (o) {
    var gid = objectGroupId(o);
    if (gid) groupIds.add(gid);
  });
  if (!groupIds.size) return Promise.resolve();

  var patches = objects
    .filter(function (o) {
      var gid = objectGroupId(o);
      return gid && groupIds.has(gid) && !o.locked;
    })
    .map(function (o) {
      return { id: o.id, patch: { meta: withGroupIdMeta(o, null) } };
    });
  if (!patches.length) return Promise.resolve();
  var undoEntry = undoEntryForUpdates(this.store.getScene(), patches);
  var self = this;
  return this.commitBatchPatches(patches, "ungroup-" + Date.now(), undoEntry).then(function () {
    self.setSelection(patches.map(function (p) { return p.id; }));
  });
};

/**
 * «Передвинуть» на виджете: выделить его как объект доски.
 * Курсор уводим из полей виджета — иначе Delete и стрелки уходили бы
 * в редактор кода, и виджет нельзя было удалить с клавиатуры.
 * Уход фокуса заодно досылает недопечатанный текст (onBlur).
 */
WbBoardScene.prototype.selectWidget = function (objectId) {
  var active = typeof document !== "undefined" ? document.activeElement : null;
  if (active && typeof active.blur === "function" && active !== document.body) {
    var card = active.closest && active.closest("[data-object-id]");
    if (card && card.getAttribute("data-object-id") === objectId) active.blur();
  }
  this.setSelection([objectId]);
  this.refreshSelectionChrome();
};

/**
 * Живой размер набираемой надписи.
 *
 * Рамка с ручками берёт размер из объекта, а объект обновлялся только
 * при отправке (через паузу) — при быстром наборе рамка отставала от поля
 * набора, и выглядело это как две несовпадающие рамки. Здесь меняем размер
 * сразу и только в своей сцене: ops не шлём, историю не трогаем.
 */
WbBoardScene.prototype.setEditingBoxHint = function (objectId, size) {
  var obj = this.findObject(objectId);
  if (!obj) return;
  var w = Math.round(Number(size && size.w) || 0);
  var h = Math.round(Number(size && size.h) || 0);
  if (w <= 0 || h <= 0) return;
  if (Math.round(Number(obj.w) || 0) === w && Math.round(Number(obj.h) || 0) === h) return;
  var scene = this.store.getScene();
  var next = {
    version: scene.version,
    objects: scene.objects.map(function (o) {
      return o.id === objectId ? Object.assign({}, o, { w: w, h: h }) : o;
    }),
  };
  this.store.applyLocalScene(next, { skipUndo: true });
  this.refreshSelectionChrome();
};

WbBoardScene.prototype.refreshSelectionChrome = function () {
  try {
    if (this.selectionFrameCtrl) {
      this.selectionFrameCtrl.update();
    }
    if (this.segmentPointHandlesCtrl) {
      this.segmentPointHandlesCtrl.update();
    }
    if (this.connectorAnchorHandlesCtrl) {
      this.connectorAnchorHandlesCtrl.update();
    }
  } catch (err) {
    if (typeof console !== "undefined" && console.error) {
      console.error("[WbBoardScene] refreshSelectionChrome failed", err);
    }
  }
};

WbBoardScene.prototype._syncFromStore = function () {
  var scene = this.store.getScene();
  var sceneChanged = scene !== this._renderedSceneRef;
  if (sceneChanged) {
    // Пока печатают в «Код», не пересобираем SVG — иначе foreignObject снимает фокус.
    // Сбой отрисовки не должен обрывать остальную синхронизацию: раньше
    // исключение здесь не давало дойти до onSelectionChange, и интерфейс
    // застывал с прежним выделением и залипшей панелью над объектами.
    if (!this._suppressCodeTypingRender) {
      try {
        this.render();
      } catch (err) {
        if (typeof console !== "undefined" && console.error) {
          console.error("[WbBoardScene] render failed", err);
        }
      }
    }
    this._renderedSceneRef = scene;
    this.onRevision(this.store.getRevision());
    this.onHistoryChange();
  } else {
    // Только выделение — без полной пересборки сцены (текст/картинки не мигают).
    this.syncSelectionClasses();
    this.refreshSelectionChrome();
  }
  this.onSelectionChange(this.store.getSelection());
};

WbBoardScene.prototype.notify = function (message) {
  if (message) this.onError(message);
};

WbBoardScene.prototype.getObjectElement = function (id) {
  if (!this.layer || !id) return null;
  // Метку объекта носит не только его корень: у таймера, калькулятора, кода и
  // SQL она стоит и на внутренних кнопках. Брать первый или последний узел
  // подряд нельзя — перетаскивание двигало кнопку вместо самого виджета, и
  // виджет прыгал на рамку только при отпускании. Берём ровно корень.
  var sel = '[data-object-id="' + id + '"]';
  var children = this.layer.children;
  for (var i = 0; i < children.length; i++) {
    var child = children[i];
    if (child.getAttribute && child.getAttribute("data-object-id") === id) {
      // Окно объекта с вёрсткой: работают с карточкой внутри него.
      if (child.classList && child.classList.contains("wb-scene-html-item")) {
        var inner = child.firstElementChild;
        var card = inner && inner.firstElementChild;
        return card || child;
      }
      return child;
    }
  }
  // Запасной путь: объект мог оказаться вложенным (например, во фрейме).
  var nodes = this.layer.querySelectorAll(sel);
  return nodes.length ? nodes[0] : null;
};

WbBoardScene.prototype.canUndo = function () {
  return this.store.undoStack.canUndo();
};

WbBoardScene.prototype.canRedo = function () {
  return this.store.undoStack.canRedo();
};

WbBoardScene.prototype.undo = function () {
  var entry = this.store.undoStack.popUndo();
  if (!entry || !this.canEdit) return Promise.resolve();
  noteDiagAction("undo");
  var prevScene = cloneScene(this.store.getScene());
  var prevRev = this.store.getRevision();
  var next = applyLocalOps(this.store.getScene(), entry.inverse);
  if (!next) return Promise.resolve();
  this.store.applyLocalScene(next, { skipUndo: true });
  var self = this;
  var committedAny = false;
  return this._postOpsChunked(entry.inverse, "undo-" + Date.now(), function () {
    committedAny = true;
  }).catch(function (err) {
    if (committedAny) {
      return self.reloadFromServer().then(function () {
        self._reportOpsError(err, "undo_partial");
        return null;
      });
    }
    self.store.rollback(prevScene, prevRev);
    return self.handleOpsFailure(err);
  });
};

WbBoardScene.prototype.redo = function () {
  var entry = this.store.undoStack.popRedo();
  if (!entry || !this.canEdit) return Promise.resolve();
  noteDiagAction("redo");
  var prevScene = cloneScene(this.store.getScene());
  var prevRev = this.store.getRevision();
  var next = applyLocalOps(this.store.getScene(), entry.forward);
  if (!next) return Promise.resolve();
  this.store.applyLocalScene(next, { skipUndo: true });
  var self = this;
  var committedAny = false;
  return this._postOpsChunked(entry.forward, "redo-" + Date.now(), function () {
    committedAny = true;
  }).catch(function (err) {
    if (committedAny) {
      return self.reloadFromServer().then(function () {
        self._reportOpsError(err, "redo_partial");
        return null;
      });
    }
    self.store.rollback(prevScene, prevRev);
    return self.handleOpsFailure(err);
  });
};

WbBoardScene.prototype._rollbackSnapshot = function (scene, revision) {
  this.store.rollback(scene, revision);
};

WbBoardScene.prototype._pushUndoEntry = function (entry) {
  if (entry) this.store.undoStack.push(entry);
};

WbBoardScene.prototype.addObject = function (object, clientOpId) {
  if (!this.canEdit || !object || !object.id) return Promise.resolve();
  if (!object.zIndex) {
    object.zIndex = nextZIndex(this.store.getScene().objects);
  }
  noteDiagAction("add_object", object.type || "object");
  var forward = [{ type: "add_object", object: object }];
  var prevScene = cloneScene(this.store.getScene());
  var prevRev = this.store.getRevision();
  var self = this;
  var next = applyLocalOps(this.store.getScene(), forward);
  if (!next) return Promise.resolve();
  this.store.applyLocalScene(next, { undoEntry: undoEntryForAdd(object) });
  this._switchToSelectAfterObjectAdd();
  return this.postOps(forward, clientOpId || "add-" + object.id).catch(function (err) {
    if (err && err.code === "revision_conflict" && !isAddOnlyOps(forward)) {
      return self.handleOpsFailure(err);
    }
    self._rollbackSnapshot(prevScene, prevRev);
    self._reportOpsError(err, object.type === "path" ? "pen_stroke" : "add_object");
    // Перо вызывает это как void — проброс давал «Uncaught (in promise)»
    // вместо понятного текста. Сообщение уже показано выше.
    return null;
  });
};

WbBoardScene.prototype.addObjectsBatch = function (objects, clientOpId) {
  if (!this.canEdit || !objects || !objects.length) return Promise.resolve();
  var sanitized = sanitizeObjectsForOps(objects);
  if (sanitized.skipped > 0) {
    this.onError(
      sanitized.skipped === 1
        ? "1 объект пропущен — не удалось подготовить к вставке"
        : sanitized.skipped + " объектов пропущено — не удалось подготовить к вставке"
    );
  }
  objects = sanitized.objects;
  if (!objects.length) return Promise.resolve();

  var sceneObjects = this.store.getScene().objects;
  var toAdd = [];
  for (var i = 0; i < objects.length; i++) {
    var object = objects[i];
    if (!object || !object.id) continue;
    if (!object.zIndex) {
      object.zIndex = nextZIndex(sceneObjects.concat(toAdd));
    }
    if (typeof object.w === "number" && !(object.w > 0)) object.w = 1;
    if (typeof object.h === "number" && !(object.h > 0)) object.h = 1;
    toAdd.push(object);
  }
  if (!toAdd.length) return Promise.resolve();

  var forward = toAdd.map(function (object) {
    return { type: "add_object", object: object };
  });
  var prevScene = cloneScene(this.store.getScene());
  var prevRev = this.store.getRevision();
  var self = this;
  var next = applyLocalOps(this.store.getScene(), forward);
  if (!next) return Promise.resolve();
  this.store.applyLocalScene(next, { undoEntry: undoEntryForAdds(toAdd) });
  this._switchToSelectAfterObjectAdd();

  var committedAny = false;

  return this._postOpsChunked(forward, clientOpId || "add-batch-" + Date.now(), function () {
    committedAny = true;
  }).catch(function (err) {
    if (err && err.code === "revision_conflict" && !isAddOnlyOps(forward)) {
      return self.handleOpsFailure(err);
    }
    if (committedAny) {
      return self.reloadFromServer().then(function () {
        self._reportOpsError(err, "add_batch_partial");
        return null;
      });
    }
    self._rollbackSnapshot(prevScene, prevRev);
    self._reportOpsError(err, "add_batch");
    // Ошибку уже показали пользователю. Пробрасывать её дальше нельзя:
    // вызывают через void/then без catch, и получался «Uncaught (in promise)»,
    // вместо понятного сообщения о переполненной доске.
    return null;
  });
};

WbBoardScene.prototype.duplicateSelection = function (offset) {
  if (!this.canEdit) return Promise.resolve();
  var dx = typeof offset === "number" ? offset : 16;
  var ids = this.getSelection();
  if (!ids.length) return Promise.resolve();

  var sources = [];
  for (var i = 0; i < ids.length; i++) {
    var obj = this.findObject(ids[i]);
    if (obj && !obj.locked) sources.push(obj);
  }
  if (!sources.length) return Promise.resolve();

  var clones = cloneSceneObjects(sources, this.getObjects(), dx);
  var self = this;
  return this.addObjectsBatch(clones, "dup-" + Date.now()).then(function () {
    self.setSelection(clones.map(function (o) {
      return o.id;
    }));
  });
};

WbBoardScene.prototype.copySelection = function () {
  if (!this.canEdit) return;
  var ids = this.getSelection();
  if (!ids.length) return;
  var objects = [];
  for (var i = 0; i < ids.length; i++) {
    var obj = this.findObject(ids[i]);
    if (obj) objects.push(JSON.parse(JSON.stringify(obj)));
  }
  if (!objects.length) return;
  setInternalClipboard(objects);
  // OS-буфер пишется в обработчике события "copy" (clipboardData.setData).
  // Здесь — запасной async write на случай если copy не дошёл.
  void writeBoardClipboardToSystem(objects);
};

WbBoardScene.prototype.uploadAssetBlob = function (blob, filename) {
  if (!this.canEdit) return Promise.reject(new Error("Read-only"));
  var form = new FormData();
  form.append("file", blob, filename || "paste.bin");
  form.append("boardId", this.boardId);
  if (this.shareToken) {
    form.append("shareToken", this.shareToken);
  }

  var opts = this.fetchOpts();
  opts.method = "POST";
  opts.body = form;

  var uploadUrl = "/api/v1/assets/upload";
  if (this.shareToken) {
    uploadUrl += "?t=" + encodeURIComponent(this.shareToken);
  } else if (this.guestEditKey) {
    uploadUrl += "?k=" + encodeURIComponent(this.guestEditKey);
  }

  return fetch(uploadUrl, opts).then(function (res) {
    return parseFetchJson(res).then(function (data) {
      if (!res.ok) throw new Error(data.error || "Upload failed");
      return data.asset;
    });
  });
};

WbBoardScene.prototype.pasteClipboard = function () {
  if (!this.canEdit || !hasInternalClipboard()) return Promise.resolve();
  var clones = preparePasteClones(this.getObjects(), 16, this.pasteAnchorPoint());
  if (!clones || !clones.length) return Promise.resolve();
  var self = this;
  return remapPasteAssets(clones, {
    fetchAsset: function (id) {
      var base = "/api/v1/assets/" + encodeURIComponent(id) + "/content";
      var attempts = [{ url: base, opts: { credentials: "include" } }];
      if (self.shareToken) {
        attempts.push({
          url: base + "?t=" + encodeURIComponent(self.shareToken),
          opts: self.fetchOpts(),
        });
      }
      if (self.guestEditKey) {
        attempts.push({
          url: base + "?k=" + encodeURIComponent(self.guestEditKey),
          opts: self.fetchOpts(),
        });
      }
      var chain = Promise.reject(new Error("no attempt"));
      attempts.forEach(function (a) {
        chain = chain.catch(function () {
          return fetch(a.url, a.opts).then(function (res) {
            if (!res.ok) throw new Error("HTTP " + res.status);
            return res;
          });
        });
      });
      return chain;
    },
    uploadBlob: function (blob, filename) {
      return self.uploadAssetBlob(blob, filename);
    },
  }).then(function (result) {
    if (result.failed > 0) {
      self.onError(
        result.failed === 1
          ? "Часть медиа не удалось перенести — проверьте доступ к исходной доске"
          : "Не удалось перенести " + result.failed + " медиафайлов с исходной доски"
      );
    }
    var toAdd = result.objects;
    return self.addObjectsBatch(toAdd, "paste-" + Date.now()).then(function () {
      self.setSelection(
        toAdd.map(function (o) {
          return o.id;
        })
      );
    });
  });
};

WbBoardScene.prototype.addTextAt = function (x, y, text, opts) {
  if (!this.canEdit) return Promise.resolve();
  var content = typeof text === "string" ? text : "";
  if (!content) return Promise.resolve();
  var size = estimateTextObjectSize(content);
  var href = opts && opts.href ? String(opts.href) : null;
  var settings = getTextSettings();
  var object = {
    id: crypto.randomUUID(),
    type: "text",
    x: Math.round(x - size.w / 2),
    y: Math.round(y - size.h / 2),
    w: size.w,
    h: size.h,
    text: content,
    style: textStyleFields(settings),
  };
  if (href) {
    object.meta = { href: href };
    object.style = Object.assign({}, object.style, { color: "#2563eb" });
  }
  applyDocumentContext(
    object,
    documentContextAt(this.getObjects(), x, y)
  );
  applyFrameContext(object, frameAtPoint(this.getObjects(), x, y));
  var self = this;
  return this.addObject(object, "text-paste-" + object.id).then(function () {
    self.setSelection([object.id]);
  });
};

WbBoardScene.prototype.adjustZOrder = function (direction) {
  if (!this.canEdit) return Promise.resolve();
  var ids = this.getSelection();
  if (!ids.length) return Promise.resolve();
  var objects = this.getObjects();
  var patches = computeZOrderPatches(objects, ids, direction);
  if (!patches.length) return Promise.resolve();
  var undoEntry = undoEntryForUpdates(this.store.getScene(), patches);
  return this.commitBatchPatches(patches, "z-" + direction + "-" + Date.now(), undoEntry);
};

/**
 * Сдвиг выделения стрелками на клавиатуре. Шаг задаёт вызывающий код:
 * обычный — на единицу, с Shift — крупный.
 */
WbBoardScene.prototype.nudgeSelection = function (dx, dy) {
  if (!this.canEdit) return Promise.resolve();
  if (!dx && !dy) return Promise.resolve();
  var ids = this.getSelection();
  if (!ids.length) return Promise.resolve();

  var patches = [];
  for (var i = 0; i < ids.length; i++) {
    var obj = this.findObject(ids[i]);
    if (!obj || obj.locked) continue;
    var patch = { x: obj.x + dx, y: obj.y + dy };
    // У линий и росчерков сдвигается ещё и каждая точка — как при перетаскивании.
    if (Array.isArray(obj.points)) {
      patch.points = obj.points.map(function (p) {
        return [p[0] + dx, p[1] + dy];
      });
    }
    patches.push({ id: obj.id, patch: patch });
  }
  if (!patches.length) return Promise.resolve();

  var undoEntry = undoEntryForUpdates(this.store.getScene(), patches);
  var self = this;
  return this.commitBatchPatches(patches, "nudge-" + Date.now(), undoEntry).then(function () {
    self.refreshSelectionChrome();
  });
};

WbBoardScene.prototype.toggleSelectionLock = function () {
  if (!this.canEdit) return Promise.resolve();
  var ids = this.getSelection();
  if (!ids.length) return Promise.resolve();
  var objects = ids
    .map(function (id) {
      return this.findObject(id);
    }, this)
    .filter(Boolean);
  if (!objects.length) return Promise.resolve();
  var allLocked = objects.every(function (o) {
    return Boolean(o.locked);
  });
  var patch = { locked: !allLocked };
  var patches = objects.map(function (o) {
    return { id: o.id, patch: patch };
  });
  var undoEntry = undoEntryForUpdates(this.store.getScene(), patches);
  return this.commitBatchPatches(patches, "lock-" + Date.now(), undoEntry);
};

WbBoardScene.prototype.alignSelection = function (mode) {
  if (!this.canEdit) return Promise.resolve();
  var ids = this.getSelection();
  if (ids.length < 2) return Promise.resolve();
  var objects = ids
    .map(function (id) {
      return this.findObject(id);
    }, this)
    .filter(Boolean);
  var patches = buildAlignPatches(objects, mode);
  if (!patches.length) return Promise.resolve();
  var undoEntry = undoEntryForUpdates(this.store.getScene(), patches);
  var self = this;
  return this.commitBatchPatches(patches, "align-" + mode + "-" + Date.now(), undoEntry).then(
    function () {
      self.refreshSelectionChrome();
    }
  );
};

WbBoardScene.prototype.distributeSelection = function (mode) {
  if (!this.canEdit) return Promise.resolve();
  var ids = this.getSelection();
  if (ids.length < 3) return Promise.resolve();
  var objects = ids
    .map(function (id) {
      return this.findObject(id);
    }, this)
    .filter(Boolean);
  var patches = buildDistributePatches(objects, mode);
  if (!patches.length) return Promise.resolve();
  var undoEntry = undoEntryForUpdates(this.store.getScene(), patches);
  var self = this;
  return this.commitBatchPatches(patches, "distribute-" + mode + "-" + Date.now(), undoEntry).then(
    function () {
      self.refreshSelectionChrome();
    }
  );
};

function patchAxisFromUndo(undoEntry, id, axis) {
  if (!undoEntry || !Array.isArray(undoEntry.inverse)) return null;
  for (var ui = 0; ui < undoEntry.inverse.length; ui++) {
    var uop = undoEntry.inverse[ui];
    if (!uop || uop.id !== id || uop.type !== "update_object") continue;
    var uv = uop.patch && uop.patch[axis];
    if (typeof uv === "number" && Number.isFinite(uv)) return uv;
  }
  return null;
}

/**
 * Исходные x/y из undo-записи, разложенные по id.
 * Тот же результат, что у patchAxisFromUndo, но без прохода по всему списку
 * на каждый объект пакета (это давало квадрат на больших выделениях).
 */
function undoOriginIndex(undoEntry) {
  var byId = new Map();
  if (!undoEntry || !Array.isArray(undoEntry.inverse)) return byId;
  for (var ui = 0; ui < undoEntry.inverse.length; ui++) {
    var uop = undoEntry.inverse[ui];
    if (!uop || uop.type !== "update_object" || !uop.patch) continue;
    var cur = byId.get(uop.id);
    if (!cur) {
      cur = { x: null, y: null };
      byId.set(uop.id, cur);
    }
    if (cur.x === null && typeof uop.patch.x === "number" && Number.isFinite(uop.patch.x)) {
      cur.x = uop.patch.x;
    }
    if (cur.y === null && typeof uop.patch.y === "number" && Number.isFinite(uop.patch.y)) {
      cur.y = uop.patch.y;
    }
  }
  return byId;
}

WbBoardScene.prototype.commitBatchPatches = function (patches, clientOpId, undoEntry) {
  if (!this.canEdit || !patches || !patches.length) return Promise.resolve();
  var commitStartedAt =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  var prevScene = cloneScene(this.store.getScene());
  var prevRev = this.store.getRevision();
  var self = this;

  var baseOps = patches.map(function (p) {
    return { type: "update_object", id: p.id, patch: p.patch };
  });
  var interim = applyLocalOps(this.store.getScene(), baseOps);
  if (!interim) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[WbBoardScene] commitBatchPatches: interim applyLocalOps failed", patches);
    }
    return Promise.resolve();
  }

  var changedIds = {};
  for (var i = 0; i < patches.length; i++) {
    changedIds[patches[i].id] = true;
  }
  var changedIdSet = new Set(Object.keys(changedIds));
  var bindingPatches = computeBindingPatches(interim.objects, changedIdSet);

  // Индексы считаем ОДИН раз на пакет. Раньше на каждый перенесённый объект
  // заново шёл линейный поиск по сцене, копирование всего массива объектов
  // (getObjects) и пересборка Set — перенос выделения из сотен объектов
  // на большой доске морозил вкладку на секунды и правки не успевали уйти.
  var sceneObjects = this.store.getScene().objects;
  var origById = new Map();
  for (var oi2 = 0; oi2 < sceneObjects.length; oi2++) {
    origById.set(sceneObjects[oi2].id, sceneObjects[oi2]);
  }
  var interimById = new Map();
  for (var ii = 0; ii < interim.objects.length; ii++) {
    interimById.set(interim.objects[ii].id, interim.objects[ii]);
  }
  var interimFrames = collectFrames(interim.objects);
  var undoOrigins = undoOriginIndex(undoEntry);

  var pdfMovePatches = [];
  var frameMovePatches = [];
  var frameReparentPatches = [];
  for (var pi = 0; pi < patches.length; pi++) {
    var pp = patches[pi];
    var orig = origById.get(pp.id);
    if (!orig) continue;
    if (pp.patch.x === undefined && pp.patch.y === undefined) continue;
    var undoOrigin = undoOrigins.get(pp.id);
    var origX = undoOrigin && undoOrigin.x !== null ? undoOrigin.x : orig.x;
    var origY = undoOrigin && undoOrigin.y !== null ? undoOrigin.y : orig.y;
    var dx = (pp.patch.x !== undefined ? pp.patch.x : origX) - origX;
    var dy = (pp.patch.y !== undefined ? pp.patch.y : origY) - origY;
    if (!dx && !dy) continue;

    if (orig.type === "pdf_document" || orig.type === "image") {
      pdfMovePatches = pdfMovePatches.concat(
        computePdfMovePatches(sceneObjects, orig.id, dx, dy, changedIdSet)
      );
    }

    if (orig.type === "frame") {
      frameMovePatches = frameMovePatches.concat(
        computeFrameMovePatches(sceneObjects, orig.id, dx, dy, changedIdSet)
      );
    } else {
      // interim уже с новыми x/y — иначе multi-select frame+child даёт ложный unparent
      var probe = interimById.get(orig.id);
      if (probe) {
        var reparent = computeFrameReparentPatch(interim.objects, probe, interimFrames);
        if (reparent) {
          frameReparentPatches.push({ id: orig.id, patch: reparent });
        }
      }
    }
  }

  var allPatches = patches.slice();
  if (bindingPatches.length) {
    allPatches = allPatches.concat(bindingPatches);
  }
  if (pdfMovePatches.length) {
    allPatches = allPatches.concat(pdfMovePatches);
  }
  if (frameMovePatches.length) {
    allPatches = allPatches.concat(frameMovePatches);
  }
  if (frameReparentPatches.length) {
    allPatches = allPatches.concat(frameReparentPatches);
  }

  var ops = allPatches.map(function (p) {
    return { type: "update_object", id: p.id, patch: p.patch };
  });

  var mergedUndo = undoEntry;
  var sidePatches = bindingPatches
    .concat(pdfMovePatches)
    .concat(frameMovePatches)
    .concat(frameReparentPatches);
  if (sidePatches.length) {
    var extraUndo = undoEntryForUpdates(this.store.getScene(), sidePatches);
    if (undoEntry) {
      mergedUndo = {
        forward: undoEntry.forward.concat(extraUndo.forward),
        inverse: extraUndo.inverse.concat(undoEntry.inverse),
      };
    } else {
      mergedUndo = extraUndo;
    }
  }

  var next = applyLocalOps(this.store.getScene(), ops);
  if (!next && allPatches.length !== patches.length) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        "[WbBoardScene] commitBatchPatches: binding apply failed, retry base patches only",
        ops
      );
    }
    ops = baseOps;
    mergedUndo = undoEntry;
    next = applyLocalOps(this.store.getScene(), ops);
  }
  if (!next) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[WbBoardScene] commitBatchPatches: final applyLocalOps failed", ops);
    }
    return Promise.resolve();
  }
  this.store.applyLocalScene(next, { undoEntry: mergedUndo });
  // Замер синхронной части: если перенос выделения снова начнёт подвешивать
  // вкладку, в консоли будет видно точное время и объём, а не догадки.
  if (typeof console !== "undefined" && console.info) {
    var syncMs = Math.round(
      (typeof performance !== "undefined" ? performance.now() : Date.now()) - commitStartedAt
    );
    console.info(
      "[bezpart-board] ops send",
      clientOpId,
      ops.length,
      "patch(es), подготовка",
      syncMs + "мс, объектов на доске",
      sceneObjects.length
    );
    if (syncMs > 100 && console.warn) {
      console.warn(
        "[bezpart-board] подготовка пакета заняла " +
          syncMs +
          "мс — на это время интерфейс не отвечает"
      );
    }
  }
  var committedAny = false;
  return this._postOpsChunked(ops, clientOpId, function () {
    committedAny = true;
  }).catch(function (err) {
    if (err && err.code === "revision_conflict") {
      return self.handleOpsFailure(err);
    }
    // Часть чанков уже на сервере — локальная инверсия дала бы рассинхрон.
    if (committedAny) {
      return self.reloadFromServer().then(function () {
        self._reportOpsError(err, "patch_partial");
        return null;
      });
    }
    if (mergedUndo && mergedUndo.inverse && mergedUndo.inverse.length) {
      var reverted = applyLocalOps(self.store.getScene(), mergedUndo.inverse);
      if (reverted) {
        self.store.applyLocalScene(reverted, { skipUndo: true });
      } else {
        self._rollbackSnapshot(prevScene, prevRev);
      }
    } else {
      self._rollbackSnapshot(prevScene, prevRev);
    }
    var msg = err && err.message ? String(err.message) : "Не удалось сохранить";
    if (
      msg === "Invalid input" ||
      msg === "unknown_message" ||
      msg === "неизвестное_сообщение" ||
      msg === "invalid_input" ||
      (err && err.code === "invalid_input")
    ) {
      msg = "Не удалось сохранить изменение (сервер отклонил запрос)";
    }
    self._reportOpsError({ code: err && err.code, message: msg }, "patch");
    // Не пробрасываем: иначе void adjustZOrder()/addObject() даёт Uncaught (in promise)
    return null;
  });
};

WbBoardScene.prototype.setSelected = function (id) {
  this.setSelection(id ? [id] : []);
};

WbBoardScene.prototype.syncSelectionClasses = function () {
  if (!this.layer) return;
  var selectedIds = this.store.getSelection();
  var set = {};
  for (var i = 0; i < selectedIds.length; i++) {
    set[selectedIds[i]] = true;
  }
  var nodes = this.layer.querySelectorAll(".wb-scene-obj");
  for (var j = 0; j < nodes.length; j++) {
    var el = nodes[j];
    var oid = el.getAttribute("data-object-id");
    if (oid && set[oid]) {
      el.classList.add("is-selected");
    } else {
      el.classList.remove("is-selected");
    }
  }
};

WbBoardScene.prototype.deleteSelected = function () {
  if (!this.canEdit || !this.store.getSelection().length) return Promise.resolve();
  var ids = this.store.getSelection().slice();
  noteDiagAction("delete_selected", "n=" + ids.length);
  return this.deleteObjects(ids, "del-sel-" + Date.now());
};

WbBoardScene.prototype.commitObjectPatch = function (id, patch, clientOpId) {
  if (!this.canEdit || !id) return Promise.resolve();
  if (!this.findObject(id)) return Promise.resolve();
  var undoEntry = undoEntryForUpdates(this.store.getScene(), [{ id: id, patch: patch }]);
  return this.commitBatchPatches([{ id: id, patch: patch }], clientOpId, undoEntry);
};

WbBoardScene.prototype.getObjectComments = function (objectId) {
  return readObjectComments(this.findObject(objectId));
};

/** Добавить комментарий к объекту (meta.comments). */
WbBoardScene.prototype.addObjectComment = function (objectId, text, authorLabel, authorPeerId) {
  if (!this.canEdit || !objectId) return Promise.resolve();
  var obj = this.findObject(objectId);
  if (!obj) return Promise.resolve();
  var cleaned = normalizeCommentText(typeof text === "string" ? text : "");
  if (!cleaned) return Promise.resolve();
  var list = readObjectComments(obj);
  if (list.length >= MAX_OBJECT_COMMENTS) {
    this.onError("Слишком много комментариев у объекта");
    return Promise.resolve();
  }
  var entry = {
    id: crypto.randomUUID(),
    text: cleaned,
    authorLabel:
      typeof authorLabel === "string" && authorLabel.trim()
        ? authorLabel.trim().slice(0, 64)
        : "Участник",
    createdAt: Date.now(),
  };
  if (typeof authorPeerId === "string" && authorPeerId) {
    entry.authorPeerId = authorPeerId;
  }
  list.push(entry);
  var meta = withCommentsMeta(obj, list);
  return this.commitObjectPatch(
    objectId,
    { meta: meta },
    "comment-add-" + entry.id
  );
};

/** Удалить свой комментарий (по id). */
WbBoardScene.prototype.deleteObjectComment = function (objectId, commentId) {
  if (!this.canEdit || !objectId || !commentId) return Promise.resolve();
  var obj = this.findObject(objectId);
  if (!obj) return Promise.resolve();
  var prev = readObjectComments(obj);
  var list = prev.filter(function (c) {
    return c.id !== commentId;
  });
  if (list.length === prev.length) return Promise.resolve();
  var meta = withCommentsMeta(obj, list);
  return this.commitObjectPatch(
    objectId,
    { meta: meta },
    "comment-del-" + commentId
  );
};

WbBoardScene.prototype.deleteObject = function (id) {
  if (!this.canEdit || !id) return Promise.resolve();
  return this.deleteObjects([id], "del-" + id + "-" + Date.now());
};

WbBoardScene.prototype.deleteObjects = function (ids, clientOpId) {
  if (!this.canEdit || !ids || !ids.length) return Promise.resolve();
  var unique = [];
  var seen = {};
  for (var i = 0; i < ids.length; i++) {
    var id = ids[i];
    if (!id || seen[id]) continue;
    seen[id] = true;
    unique.push(id);
  }
  if (!unique.length) return Promise.resolve();

  var expanded = unique.slice();
  var expandSeen = {};
  for (var ei = 0; ei < unique.length; ei++) {
    expandSeen[unique[ei]] = true;
  }
  for (var ej = 0; ej < unique.length; ej++) {
    var cand = this.findObject(unique[ej]);
    if (!cand || (cand.type !== "pdf_document" && cand.type !== "image")) continue;
    var annIds = collectPdfAnnotationIds(this.getObjects(), cand.id);
    for (var ak = 0; ak < annIds.length; ak++) {
      var aid = annIds[ak];
      if (!expandSeen[aid]) {
        expandSeen[aid] = true;
        expanded.push(aid);
      }
    }
  }
  unique = expanded;

  var objects = [];
  for (var j = 0; j < unique.length; j++) {
    var obj = this.findObject(unique[j]);
    if (obj) objects.push(obj);
  }
  if (!objects.length) return Promise.resolve();

  var deleteIdSet = {};
  for (var di = 0; di < unique.length; di++) {
    deleteIdSet[unique[di]] = true;
  }

  // Удаление рамки: снять frameId у детей (сами объекты остаются)
  var frameClearPatches = [];
  var frameClearSeen = {};
  for (var fj = 0; fj < objects.length; fj++) {
    if (objects[fj].type !== "frame") continue;
    var childIds = collectFrameChildIds(this.getObjects(), objects[fj].id);
    for (var ck = 0; ck < childIds.length; ck++) {
      var childId = childIds[ck];
      if (deleteIdSet[childId] || frameClearSeen[childId]) continue;
      frameClearSeen[childId] = true;
      frameClearPatches.push({ id: childId, patch: { frameId: null } });
    }
  }

  var bindingPatchMap = {};
  for (var bi = 0; bi < unique.length; bi++) {
    var clears = clearBindingsToObject(this.getObjects(), unique[bi]);
    for (var ci = 0; ci < clears.length; ci++) {
      var cp = clears[ci];
      if (!bindingPatchMap[cp.id]) {
        bindingPatchMap[cp.id] = { id: cp.id, patch: {} };
      }
      var cur = bindingPatchMap[cp.id].patch;
      if (cp.patch.bindings === null) {
        bindingPatchMap[cp.id].patch.bindings = null;
      } else if (cp.patch.bindings) {
        bindingPatchMap[cp.id].patch.bindings = Object.assign(
          cur.bindings || {},
          cp.patch.bindings
        );
      }
    }
  }
  var bindingPatches = Object.keys(bindingPatchMap).map(function (id) {
    return bindingPatchMap[id];
  });

  var sideUpdatePatches = bindingPatches.concat(frameClearPatches);

  var prevScene = cloneScene(this.store.getScene());
  var prevRev = this.store.getRevision();
  var prevSelected = this.store.getSelection().slice();
  var undoEntry = undoEntryForDeletes(objects);
  if (sideUpdatePatches.length) {
    var bindingUndo = undoEntryForUpdates(this.store.getScene(), sideUpdatePatches);
    undoEntry = {
      forward: bindingUndo.forward.concat(undoEntry.forward),
      inverse: undoEntry.inverse.concat(bindingUndo.inverse),
    };
  }

  var forward = sideUpdatePatches
    .map(function (p) {
      return { type: "update_object", id: p.id, patch: p.patch };
    })
    .concat(
      objects.map(function (o) {
        return { type: "delete_object", id: o.id };
      })
    );
  var next = applyLocalOps(this.store.getScene(), forward);
  if (!next) return Promise.resolve();

  this.store.applyLocalScene(next, { undoEntry: undoEntry });
  var removed = {};
  for (var k = 0; k < objects.length; k++) {
    removed[objects[k].id] = true;
  }
  if (prevSelected.some(function (sid) {
    return removed[sid];
  })) {
    this.store.setSelection(
      prevSelected.filter(function (sid) {
        return !removed[sid];
      })
    );
  }

  var self = this;
  // Не soft-delete PDF page assets: Delete → Undo восстанавливает объект, а файлы уже deleted_at → белый слайд.
  // Картинки/аудио по-прежнему помечаем.
  var mediaAssetIds = [];
  for (var pi = 0; pi < objects.length; pi++) {
    var delObj = objects[pi];
    if (delObj && delObj.type === "pdf_document") continue;
    mediaAssetIds = mediaAssetIds.concat(collectMediaAssetIds(delObj));
  }
  var committedAny = false;
  return this._postOpsChunked(forward, clientOpId || "del-batch-" + Date.now(), function () {
    committedAny = true;
  })
    .then(function () {
      if (mediaAssetIds.length) {
        markAssetsDeleted({
          boardId: self.boardId,
          assetIds: mediaAssetIds,
          shareToken: self.shareToken,
          guestEditKey: self.guestEditKey,
          useCredentials: self.useCredentials,
        });
      }
    })
    .catch(function (err) {
    if (err && err.code === "revision_conflict") {
      return self.handleOpsFailure(err);
    }
    // Часть удалений уже применилась на сервере — откат вернул бы удалённое.
    if (committedAny) {
      return self.reloadFromServer().then(function () {
        self._reportOpsError(err, "delete_partial");
        return null;
      });
    }
    self.store.rollback(prevScene, prevRev);
    self.store.setSelection(prevSelected);
    // Через boardSaveErrorMessage, а не сырое message: у переполненной доски
    // должен быть человеческий текст, а не код ошибки.
    self._reportOpsError(err, "delete");
    return null;
  });
};

WbBoardScene.prototype.opsUrl = function () {
  if (this.guestEditKey) {
    return (
      "/api/v1/guest/boards/" +
      encodeURIComponent(this.boardId) +
      "/ops?k=" +
      encodeURIComponent(this.guestEditKey)
    );
  }
  var url = "/api/v1/boards/" + encodeURIComponent(this.boardId) + "/ops";
  if (this.shareToken) {
    url += "?t=" + encodeURIComponent(this.shareToken);
  }
  return url;
};

WbBoardScene.prototype.stateUrl = function () {
  if (this.guestEditKey) {
    return (
      "/api/v1/guest/boards/" +
      encodeURIComponent(this.boardId) +
      "/state?k=" +
      encodeURIComponent(this.guestEditKey)
    );
  }
  var url = "/api/v1/boards/" + encodeURIComponent(this.boardId) + "/state";
  if (this.shareToken) {
    url += "?t=" + encodeURIComponent(this.shareToken);
  }
  return url;
};

WbBoardScene.prototype.fetchOpts = function () {
  return this.useCredentials ? { credentials: "include" } : { credentials: "omit" };
};

WbBoardScene.prototype.mount = function () {
  // iOS: палец и стилус иначе иногда начинают выделять текст всей страницы.
  this._releaseSelectionGuard = installBoardSelectionGuard();

  var layer = createSceneSvgLayer();
  this.layer = layer;
  this.stage.insertBefore(layer, this.stage.firstChild);

  var overlay = document.createElement("div");
  overlay.className = "wb-board-selection-overlay";
  overlay.setAttribute("aria-hidden", "true");
  this.selectionOverlay = overlay;
  this.stage.appendChild(overlay);

  this.render();

  if (typeof console !== "undefined" && console.info) {
    console.info("[bezpart-board] editor build:", EDITOR_CLIENT_BUILD);
    if (typeof window !== "undefined") {
      window.__BEZPART_EDITOR_BUILD__ = EDITOR_CLIENT_BUILD;
    }
  }

  var self = this;
  this._storeUnsub = this.store.subscribe(function () {
    self._syncFromStore();
  });

  var viewportEl = this.stage.closest(".wb-board-viewport");
  var hitSurface = viewportEl || this.stage;

  this._pastePointerHit = hitSurface;
  this._onPastePointerTrack = function (ev) {
    if (typeof ev.clientX !== "number" || typeof ev.clientY !== "number") return;
    self._lastPointerClient = { x: ev.clientX, y: ev.clientY };
  };
  hitSurface.addEventListener("pointerdown", this._onPastePointerTrack, true);
  hitSurface.addEventListener("pointermove", this._onPastePointerTrack, true);

  // При отсечении сцена собрана под конкретную область. Когда камера уходит
  // за её пределы, нужен новый кадр — но не на каждый пиксель панорамы,
  // иначе вернём ту же нагрузку, от которой уходили.
  this._onCameraMoved = function () {
    if (!self._culledView || self._cullRaf) return;
    self._cullRaf = requestAnimationFrame(function () {
      self._cullRaf = 0;
      if (!self._culledView) return;
      var view = self._visibleWorldBounds();
      if (!view) return;
      var c = self._culledView;
      var inside =
        view.x >= c.x &&
        view.y >= c.y &&
        view.x + view.w <= c.x + c.w &&
        view.y + view.h <= c.y + c.h;
      if (!inside) self.render();
    });
  };
  window.addEventListener("wb:live-viewport", this._onCameraMoved);

  this.selectionFrameCtrl = createSelectionFrame(overlay, {
    canEdit: this.canEdit,
    getActiveTool: function () {
      return self.getActiveTool();
    },
    getSelection: function () {
      return self.getSelection();
    },
    getObjects: function () {
      return self.getObjects();
    },
    findObject: function (id) {
      return self.findObject(id);
    },
    getObjectElement: function (id) {
      return self.getObjectElement(id);
    },
    stagePoint: function (clientX, clientY) {
      return stagePoint(self, clientX, clientY);
    },
    getSvgDefs: function () {
      return sceneSvgDefs(self.layer);
    },
    isTextEditing: function () {
      return self.isTextEditing();
    },
    commitBatchPatches: function (patches, clientOpId, undoEntry) {
      return self.commitBatchPatches(patches, clientOpId, undoEntry);
    },
    onLayoutChange: function () {
      if (typeof self.onSelectionChromeLayout === "function") {
        self.onSelectionChromeLayout();
      }
    },
  });
  this.segmentPointHandlesCtrl = createSegmentPointHandles(overlay, {
    canEdit: this.canEdit,
    getActiveTool: function () {
      return self.getActiveTool();
    },
    getSelection: function () {
      return self.getSelection();
    },
    getObjects: function () {
      return self.getObjects();
    },
    findObject: function (id) {
      return self.findObject(id);
    },
    getObjectElement: function (id) {
      return self.getObjectElement(id);
    },
    stagePoint: function (clientX, clientY) {
      return stagePoint(self, clientX, clientY);
    },
    getSvgDefs: function () {
      return sceneSvgDefs(self.layer);
    },
    commitBatchPatches: function (patches, clientOpId, undoEntry) {
      return self.commitBatchPatches(patches, clientOpId, undoEntry);
    },
    onLayoutChange: function () {
      if (typeof self.onSelectionChromeLayout === "function") {
        self.onSelectionChromeLayout();
      }
    },
  });
  this.connectorAnchorHandlesCtrl = createConnectorAnchorHandles(overlay, {
    canEdit: this.canEdit,
    selectionOverlay: overlay,
    getActiveTool: function () {
      return self.getActiveTool();
    },
    getSelection: function () {
      return self.getSelection();
    },
    getObjects: function () {
      return self.getObjects();
    },
    findObject: function (id) {
      return self.findObject(id);
    },
    stagePoint: function (clientX, clientY) {
      return stagePoint(self, clientX, clientY);
    },
    addObject: function (object, clientOpId) {
      return self.addObject(object, clientOpId);
    },
    setSelection: function (ids) {
      self.setSelection(ids);
    },
    refreshSelectionChrome: function () {
      self.refreshSelectionChrome();
    },
    onLayoutChange: function () {
      if (typeof self.onSelectionChromeLayout === "function") {
        self.onSelectionChromeLayout();
      }
    },
  });
  this.refreshSelectionChrome();

  this.toolController = createToolController({
    canEdit: this.canEdit,
    stage: this.stage,
    hitSurface: hitSurface,
    layer: this.layer,
    getActiveTool: function () {
      return self.getActiveTool();
    },
    setActiveTool: function (toolId) {
      // Из инструментов (после вставки) — обычный switch; force не нужен
      self.setActiveTool(toolId, false);
    },
    stagePoint: function (clientX, clientY) {
      return stagePoint(self, clientX, clientY);
    },
    selectionOverlay: overlay,
    getObjects: function () {
      return self.getObjects();
    },
    getSelection: function () {
      return self.getSelection();
    },
    setSelection: function (ids) {
      self.setSelection(ids);
    },
    refreshSelectionChrome: function () {
      self.refreshSelectionChrome();
    },
    getObjectElement: function (id) {
      return self.getObjectElement(id);
    },
    findObject: function (id) {
      return self.findObject(id);
    },
    deleteObject: function (id) {
      return self.deleteObject(id);
    },
    deleteObjects: function (ids, clientOpId) {
      return self.deleteObjects(ids, clientOpId);
    },
    commitObjectPatch: function (id, patch, clientOpId) {
      return self.commitObjectPatch(id, patch, clientOpId);
    },
    commitBatchPatches: function (patches, clientOpId, undoEntry) {
      return self.commitBatchPatches(patches, clientOpId, undoEntry);
    },
    addObject: function (object, clientOpId) {
      return self.addObject(object, clientOpId);
    },
    panBy: function (dx, dy) {
      self.panBy(dx, dy);
    },
    endPanGesture: function () {
      self.endPanGesture();
    },
    notify: function (message) {
      self.notify(message);
    },
    isInteractionBlocked: function () {
      return self.isInteractionBlocked();
    },
    requestTextEdit: function (id) {
      self.requestTextEdit(id);
    },
  });

  this.toolController.setActiveTool(this.activeTool);

  if (this.canEdit) {
    this.stage.classList.add("is-editable");
  }
};

WbBoardScene.prototype._markEarlyRemoteOps = function (ops) {
  if (!ops || !ops.length) return;
  for (var i = 0; i < ops.length; i++) {
    var op = ops[i];
    if (op && op.type === "add_object" && op.object && op.object.id) {
      this._earlyRemoteObjectIds[op.object.id] = true;
    }
  }
};

WbBoardScene.prototype._clearEarlyRemoteOps = function (ops) {
  if (!ops || !ops.length) return;
  for (var i = 0; i < ops.length; i++) {
    var op = ops[i];
    if (op && op.type === "add_object" && op.object && op.object.id) {
      delete this._earlyRemoteObjectIds[op.object.id];
    }
  }
};

WbBoardScene.prototype._mergeEarlyRemoteIntoScene = function (serverScene) {
  var pending = this._earlyRemoteObjectIds;
  var pendingIds = Object.keys(pending);
  if (!pendingIds.length) return serverScene;
  var serverObjs = (serverScene && serverScene.objects) || [];
  var byId = {};
  for (var i = 0; i < serverObjs.length; i++) {
    byId[serverObjs[i].id] = true;
  }
  var extras = [];
  var localObjs = this.store.getScene().objects || [];
  for (var j = 0; j < localObjs.length; j++) {
    var lo = localObjs[j];
    if (!lo || !lo.id) continue;
    if (!pending[lo.id]) continue;
    if (byId[lo.id]) {
      delete pending[lo.id];
      continue;
    }
    extras.push(lo);
  }
  if (!extras.length) return serverScene;
  return {
    version: (serverScene && serverScene.version) || 2,
    objects: serverObjs.concat(extras),
  };
};

WbBoardScene.prototype.bindRealtime = function (rt) {
  var self = this;
  this.realtime = rt;

  function opsAddIdsPresent(scene, ops) {
    if (!ops || !ops.length) return false;
    for (var i = 0; i < ops.length; i++) {
      var op = ops[i];
      if (!op || op.type !== "add_object" || !op.object || !op.object.id) return false;
      if (!scene.objects.some(function (o) { return o.id === op.object.id; })) return false;
    }
    return true;
  }

  function invertAddOps(ops) {
    var inverse = [];
    for (var i = 0; i < ops.length; i++) {
      var op = ops[i];
      if (op && op.type === "add_object" && op.object && op.object.id) {
        inverse.push({ type: "delete_object", id: String(op.object.id) });
      }
    }
    return inverse;
  }

  /** Пока сервер пишет snapshot — показать add_object сразу (без revision). */
  rt.onRemoteOpsEarly = function (msg) {
    var ops = Array.isArray(msg.ops) ? msg.ops : null;
    if (!ops || !ops.length) return;
    if (!ops.every(function (op) { return op && op.type === "add_object"; })) return;
    var next = applyLocalOps(self.store.getScene(), ops);
    if (!next) return;
    self._markEarlyRemoteOps(ops);
    self.store.applyLocalScene(next, { skipUndo: true });
  };

  rt.onRemoteOpsRevert = function (msg) {
    var ops = Array.isArray(msg.ops) ? msg.ops : null;
    if (!ops || !ops.length) return;
    var inverse = invertAddOps(ops);
    if (!inverse.length) return;
    self._clearEarlyRemoteOps(ops);
    var next = applyLocalOps(self.store.getScene(), inverse);
    if (!next) {
      void self.reloadFromServer().catch(function () {});
      return;
    }
    self.store.applyLocalScene(next, { skipUndo: true });
  };

  rt.onRemoteOps = function (msg) {
    if (typeof msg.revision !== "number") return;
    var local = self.store.getRevision();
    if (msg.revision <= local) return;

    var ops = Array.isArray(msg.ops) ? msg.ops : null;
    if (ops && ops.length && msg.revision === local + 1) {
      var next = applyLocalOps(self.store.getScene(), ops);
      if (next) {
        // Свои ops, ещё не подтверждённые сервером, лягут на сервере после этих —
        // значит, и у нас должны быть сверху. Иначе чужой текст затирал бы наш
        // свежий набор в виджете, а сервер в итоге хранил бы наш.
        next = self._reapplyPendingOps(next);
        self._clearEarlyRemoteOps(ops);
        // Не чистим undo: иначе при совместном рисовании Ctrl+Z сразу «умирает».
        self.store.setRemoteSnapshot(msg.revision, next, { clearUndo: false });
        return;
      }
      // Уже показали через ops_early — только подтверждаем revision.
      if (opsAddIdsPresent(self.store.getScene(), ops)) {
        self._clearEarlyRemoteOps(ops);
        self.store.setRemoteSnapshot(msg.revision, self.store.getScene(), { clearUndo: false });
        return;
      }
    }

    if (msg.scene) {
      self.applySnapshot({ revision: msg.revision, scene: msg.scene }, { remote: true });
      return;
    }

    void self.reloadFromServer().catch(function () {});
  };
};

WbBoardScene.prototype.destroy = function () {
  if (this._releaseSelectionGuard) {
    this._releaseSelectionGuard();
    this._releaseSelectionGuard = null;
  }
  if (this._pastePointerHit && this._onPastePointerTrack) {
    this._pastePointerHit.removeEventListener("pointerdown", this._onPastePointerTrack, true);
    this._pastePointerHit.removeEventListener("pointermove", this._onPastePointerTrack, true);
  }
  this._pastePointerHit = null;
  this._onPastePointerTrack = null;
  this._lastPointerClient = null;
  if (this._storeUnsub) {
    this._storeUnsub();
    this._storeUnsub = null;
  }
  if (this.selectionFrameCtrl) {
    this.selectionFrameCtrl.destroy();
    this.selectionFrameCtrl = null;
  }
  if (this.segmentPointHandlesCtrl) {
    this.segmentPointHandlesCtrl.destroy();
    this.segmentPointHandlesCtrl = null;
  }
  if (this.connectorAnchorHandlesCtrl) {
    this.connectorAnchorHandlesCtrl.destroy();
    this.connectorAnchorHandlesCtrl = null;
  }
  if (this.toolController) {
    this.toolController.destroy();
    this.toolController = null;
  }
  if (this.realtime) {
    this.realtime.onRemoteOps = null;
    this.realtime.onRemoteOpsEarly = null;
    this.realtime.onRemoteOpsRevert = null;
    this.realtime = null;
  }
  if (this._onCameraMoved) {
    window.removeEventListener("wb:live-viewport", this._onCameraMoved);
    this._onCameraMoved = null;
  }
  if (this._cullRaf) {
    cancelAnimationFrame(this._cullRaf);
    this._cullRaf = 0;
  }
  if (this._opsQueue) {
    this._opsQueue.reset();
  }
  this._pendingOps = [];
  disposeAllTimers();
  if (this.layer && this.layer.parentNode) {
    this.layer.parentNode.removeChild(this.layer);
  }
  this.layer = null;
};

/** Видимая область в координатах сцены — null, если камера ещё не готова. */
WbBoardScene.prototype._visibleWorldBounds = function () {
  var viewportEl = this.stage && this.stage.closest(".wb-board-viewport");
  if (!viewportEl || !this.mapClientToStage) return null;
  var self = this;
  try {
    return visibleWorldBoundsFromViewport(function (cx, cy) {
      return stagePoint(self, cx, cy);
    }, viewportEl);
  } catch (err) {
    return null;
  }
};

WbBoardScene.prototype.render = function () {
  if (!this.layer) return;
  var self = this;
  // Длительность перерисовки — в отчёт «Проблема» (lib/board/perfProbe).
  var renderStartedAt = performance.now();
  var allObjects = this.store.getScene().objects;
  for (var i = 0; i < allObjects.length; i++) {
    resolveAndApplySegment(allObjects[i], allObjects);
  }
  // На больших досках рисуем только то, что рядом с экраном (плюс запас).
  // Выделенное, редактируемое и живые виджеты отбираются всегда — см. sceneCulling.
  var view = this._visibleWorldBounds();
  var objects = cullSceneObjects(allObjects, {
    view: view,
    selectedIds: new Set(this.store.getSelection()),
    editingId: this._textEditId || null,
  });
  this._lastRenderedCount = objects.length;
  // Видно, работает ли отсечение и насколько: без этого о причинах лага
  // при масштабировании приходится гадать.
  if (typeof window !== "undefined") {
    window.__wbRenderStats = {
      всегоНаДоске: allObjects.length,
      рисуется: objects.length,
      отсечено: allObjects.length - objects.length,
      узловВСлое: this.layer.getElementsByTagName("*").length,
    };
  }
  // Область, ради которой собран этот кадр: пока камера внутри неё,
  // перерисовывать при панораме не нужно.
  this._culledView = objects === allObjects || !view ? null : expandView(view);
  renderSvgSceneLayer(this.layer, objects, {
    surfaceObjects: allObjects,
    assetContentUrl: function (assetId) {
      return self.assetContentUrl(assetId);
    },
    pagePreviewUrl: function (objectId, pageIndex) {
      return getPdfPagePreviewUrl(objectId, pageIndex);
    },
    useCredentials: self.useCredentials,
    canEdit: self.canEdit,
    textEditingId: self._textEditId || null,
    canPdfPageChange: function (objectId) {
      return self.canControlPdfPage(objectId);
    },
    onPdfPageChange: function (objectId, page) {
      void self.setPdfPage(objectId, page);
    },
    onTimerAction: function (objectId, action, payload) {
      void self.applyTimerAction(objectId, action, payload);
    },
    onTimerSelect: function (objectId) {
      self.selectWidget(objectId);
    },
    onCalculatorAction: function (objectId, action) {
      void self.applyCalculatorAction(objectId, action);
    },
    onCalculatorSelect: function (objectId) {
      self.selectWidget(objectId);
    },
    onCodeAction: function (objectId, action) {
      void self.applyCodeAction(objectId, action);
    },
    onCodeSelect: function (objectId) {
      self.selectWidget(objectId);
    },
    onSqlAction: function (objectId, action) {
      void self.applySqlAction(objectId, action);
    },
    onSqlSelect: function (objectId) {
      self.selectWidget(objectId);
    },
    onAudioAction: function (objectId, action) {
      void self.applyAudioAction(objectId, action);
    },
  });
  // Интервалы таймеров, объектов которых больше нет на доске.
  var liveTimerIds = new Set();
  for (var ti = 0; ti < allObjects.length; ti++) {
    if (allObjects[ti].type === "timer") liveTimerIds.add(allObjects[ti].id);
  }
  disposeTimersExcept(liveTimerIds);
  this.syncCommentBadges();
  this.syncSelectionClasses();
  this.refreshSelectionChrome();
  noteSceneRender(performance.now() - renderStartedAt);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("wb:scene-rendered"));
  }
};

WbBoardScene.prototype.syncCommentBadges = function () {
  if (!this.selectionOverlay) return;
  var host = this.selectionOverlay.querySelector(".wb-comment-badges");
  if (!host) {
    host = document.createElement("div");
    host.className = "wb-comment-badges";
    host.style.cssText =
      "position:absolute;left:0;top:0;width:0;height:0;overflow:visible;pointer-events:none;";
    this.selectionOverlay.appendChild(host);
  }
  while (host.firstChild) host.removeChild(host.firstChild);

  var objects = this.getObjects();
  for (var i = 0; i < objects.length; i++) {
    var obj = objects[i];
    var count = objectCommentCount(obj);
    if (!count) continue;
    var w = typeof obj.w === "number" ? obj.w : 0;
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "wb-object-comment-badge";
    btn.textContent = count > 99 ? "99+" : String(count);
    btn.title = "Комментарии (" + count + ")";
    btn.setAttribute("aria-label", "Открыть комментарии, " + count);
    btn.dataset.objectId = obj.id;
    btn.style.left = Math.round(obj.x + Math.max(w, 24) - 4) + "px";
    btn.style.top = Math.round(obj.y - 8) + "px";
    btn.addEventListener("mousedown", function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    });
    btn.addEventListener("click", function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      var id =
        ev.currentTarget && ev.currentTarget.dataset
          ? ev.currentTarget.dataset.objectId
          : null;
      if (!id) return;
      window.dispatchEvent(
        new CustomEvent("wb:open-object-comments", { detail: { objectId: id } })
      );
    });
    host.appendChild(btn);
  }
};

WbBoardScene.prototype.assetContentUrl = function (assetId) {
  var url = "/api/v1/assets/" + encodeURIComponent(assetId) + "/content";
  if (this.shareToken) {
    url += "?t=" + encodeURIComponent(this.shareToken);
  } else if (this.guestEditKey) {
    url += "?k=" + encodeURIComponent(this.guestEditKey);
  }
  return url;
};

/**
 * Видимая область доски в мировых единицах (для размера вставляемой картинки).
 * scale — сколько экранных пикселей приходится на мировую единицу: по нему
 * считается, во сколько раз картинку можно растянуть без мыла.
 */
WbBoardScene.prototype.visibleWorldSize = function () {
  var viewportEl = this.stage ? this.stage.closest(".wb-board-viewport") : null;
  if (!viewportEl) return null;
  var rect = viewportEl.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  try {
    var a = stagePoint(this, rect.left, rect.top);
    var b = stagePoint(this, rect.right, rect.bottom);
    var w = Math.abs(b.x - a.x);
    var h = Math.abs(b.y - a.y);
    if (!isFinite(w) || !isFinite(h) || w < 1 || h < 1) return null;
    return { w: w, h: h, scale: rect.width / w };
  } catch (_e) {
    return null;
  }
};

/**
 * Размер картинки на доске: доля видимой области, а не пиксели оригинала.
 * Вся арифметика в imageInsertSize — там же тесты.
 */
WbBoardScene.prototype.scaleImageSize = function (width, height) {
  return imageInsertSize({ w: width, h: height }, this.visibleWorldSize());
};

WbBoardScene.prototype.addImageAt = function (x, y, asset, naturalW, naturalH) {
  if (!this.canEdit || !asset || !asset.id) return Promise.resolve();
  var size = this.scaleImageSize(naturalW, naturalH);
  var object = {
    id: crypto.randomUUID(),
    type: "image",
    x: Math.round(x - size.w / 2),
    y: Math.round(y - size.h / 2),
    w: size.w,
    h: size.h,
    assetId: asset.id,
    // Картинка ложится под всё: на ней пишут, а не её кладут поверх записей.
    zIndex: bottomZIndex(this.getObjects()),
  };
  applyFrameContext(object, frameAtPoint(this.getObjects(), x, y));
  return this.addObject(object, "img-" + object.id);
};

WbBoardScene.prototype.uploadImageFile = function (file, x, y) {
  if (!this.canEdit) return Promise.reject(new Error("Read-only"));
  var self = this;
  var form = new FormData();
  form.append("file", file);
  form.append("boardId", this.boardId);
  if (this.shareToken) {
    form.append("shareToken", this.shareToken);
  }

  var opts = this.fetchOpts();
  opts.method = "POST";
  opts.body = form;

  var uploadUrl = "/api/v1/assets/upload";
  if (this.shareToken) {
    uploadUrl += "?t=" + encodeURIComponent(this.shareToken);
  } else if (this.guestEditKey) {
    uploadUrl += "?k=" + encodeURIComponent(this.guestEditKey);
  }

  return fetch(uploadUrl, opts)
    .then(function (res) {
      return parseFetchJson(res).then(function (data) {
        if (!res.ok) throw new Error(data.error || "Upload failed");
        return data.asset;
      });
    })
    .then(function (asset) {
      return new Promise(function (resolve, reject) {
        var probe = new Image();
        probe.onload = function () {
          resolve({
            asset: asset,
            w: probe.naturalWidth,
            h: probe.naturalHeight,
          });
        };
        probe.onerror = function () {
          resolve({ asset: asset, w: 200, h: 150 });
        };
        probe.src = self.assetContentUrl(asset.id);
      });
    })
    .then(function (payload) {
      return self.addImageAt(x, y, payload.asset, payload.w, payload.h);
    })
    .catch(function (err) {
      self.onError(err.message || "Не удалось загрузить изображение");
      throw err;
    });
};

WbBoardScene.prototype.getSessionPeerId = function () {
  return this.realtime && this.realtime.myPeerId ? this.realtime.myPeerId : null;
};

WbBoardScene.prototype.canControlPdfPage = function (objectId) {
  var obj = this.findObject(objectId);
  if (!obj) return false;
  return canControlPdfDocument(obj, this.getSessionPeerId(), this.canEdit);
};

WbBoardScene.prototype.startPdfBroadcast = function (objectId) {
  if (!this.canEdit || !objectId) return Promise.resolve();
  var peerId = this.getSessionPeerId();
  if (!peerId) {
    this.onError("Подождите подключения к доске");
    return Promise.resolve();
  }
  var obj = this.findObject(objectId);
  if (!obj || obj.type !== "pdf_document") return Promise.resolve();
  var patches = [];
  var objects = this.getObjects();
  for (var i = 0; i < objects.length; i++) {
    var o = objects[i];
    if (o.type === "pdf_document" && o.id !== objectId && o.displayMode === "presentation") {
      patches.push({ id: o.id, patch: { displayMode: "board" } });
    }
  }
  patches.push({
    id: objectId,
    patch: { displayMode: "presentation", presentationLeaderId: peerId },
  });
  if (patches.length === 1) {
    return this.commitObjectPatch(objectId, patches[0].patch, "pdf-broadcast-start-" + Date.now());
  }
  return this.commitBatchPatches(patches, "pdf-broadcast-start-" + Date.now());
};

WbBoardScene.prototype.stopPdfBroadcast = function (objectId) {
  if (!objectId) return Promise.resolve();
  var obj = this.findObject(objectId);
  if (!obj || obj.type !== "pdf_document") return Promise.resolve();
  if (obj.displayMode !== "presentation") return Promise.resolve();
  if (!this.canControlPdfPage(objectId)) return Promise.resolve();
  return this.commitObjectPatch(
    objectId,
    { displayMode: "board" },
    "pdf-broadcast-stop-" + Date.now()
  );
};

WbBoardScene.prototype.setPdfPage = function (objectId, page) {
  if (!this.canControlPdfPage(objectId)) return Promise.resolve();
  var obj = this.findObject(objectId);
  if (!obj || obj.type !== "pdf_document") return Promise.resolve();
  var pageCount = typeof obj.pageCount === "number" ? obj.pageCount : 1;
  var next = Math.max(0, Math.min(pageCount - 1, page));
  var peerId = this.getSessionPeerId();
  var patch = { currentPage: next };
  // Подхватить ведущего после reconnect (иначе кнопки «Вперёд» остаются мёртвыми у старого peerId).
  if (
    obj.displayMode === "presentation" &&
    peerId &&
    obj.presentationLeaderId !== peerId &&
    this.canEdit
  ) {
    patch.presentationLeaderId = peerId;
  }
  if (obj.currentPage === next && !patch.presentationLeaderId) return Promise.resolve();
  return this.commitObjectPatch(objectId, patch, "pdf-page-" + Date.now());
};

WbBoardScene.prototype.applyTimerAction = function (objectId, action, payload) {
  var obj = this.findObject(objectId);
  if (!obj || obj.type !== "timer") return Promise.resolve();

  // finish может прислать любой клиент (идемпотентно), остальное — только редакторы
  if (action !== "finish" && !this.canEdit) return Promise.resolve();

  var durationMs = typeof obj.durationMs === "number" ? obj.durationMs : 5 * 60 * 1000;
  var remainingMs = typeof obj.remainingMs === "number" ? obj.remainingMs : durationMs;
  var status = obj.status || "idle";
  var endsAt = typeof obj.endsAt === "number" ? obj.endsAt : null;
  var patch = null;
  var opId = "timer-" + action + "-" + Date.now();

  if (action === "setDuration") {
    var nextDur =
      payload && typeof payload.durationMs === "number" ? payload.durationMs : durationMs;
    nextDur = Math.max(1000, Math.min(24 * 60 * 60 * 1000, Math.round(nextDur)));
    if (status === "running") return Promise.resolve();
    patch = {
      durationMs: nextDur,
      remainingMs: nextDur,
      status: "idle",
      endsAt: null,
    };
  } else if (action === "start") {
    var base =
      status === "finished" || remainingMs <= 0
        ? durationMs
        : Math.max(0, remainingMs);
    if (base <= 0) base = durationMs;
    patch = {
      remainingMs: base,
      status: "running",
      endsAt: Date.now() + base,
    };
  } else if (action === "pause") {
    if (status !== "running") return Promise.resolve();
    var left = endsAt != null ? Math.max(0, endsAt - Date.now()) : remainingMs;
    patch = {
      remainingMs: left,
      status: left <= 0 ? "finished" : "paused",
      endsAt: null,
    };
  } else if (action === "reset") {
    patch = {
      remainingMs: durationMs,
      status: "idle",
      endsAt: null,
    };
  } else if (action === "finish") {
    if (status === "finished" && remainingMs === 0 && endsAt == null) {
      return Promise.resolve();
    }
    patch = {
      remainingMs: 0,
      status: "finished",
      endsAt: null,
    };
  } else {
    return Promise.resolve();
  }

  return this.commitObjectPatch(objectId, patch, opId);
};

WbBoardScene.prototype.addTimerAt = function (x, y) {
  if (!this.canEdit) return Promise.resolve();
  var durationMs = 5 * 60 * 1000;
  var w = TIMER_DEFAULT_SIZE.w;
  var h = TIMER_DEFAULT_SIZE.h;
  var object = {
    id: crypto.randomUUID(),
    type: "timer",
    x: Math.round(x - w / 2),
    y: Math.round(y - h / 2),
    w: w,
    h: h,
    durationMs: durationMs,
    remainingMs: durationMs,
    status: "idle",
    zIndex: nextZIndex(this.getObjects()),
  };
  applyFrameContext(object, frameAtPoint(this.getObjects(), x, y));
  var self = this;
  return this.addObject(object, "timer-" + object.id).then(function () {
    self.setSelection([object.id]);
    return object;
  });
};

WbBoardScene.prototype.applyCalculatorAction = function (objectId, action) {
  if (!this.canEdit) return Promise.resolve();
  var obj = this.findObject(objectId);
  if (!obj || obj.type !== "calculator") return Promise.resolve();

  var prev = readCalculatorState(obj);
  var next = applyCalculatorAction(prev, action);
  var patch = calculatorStateToPatch(next);
  var same =
    prev.mode === next.mode &&
    prev.expr === next.expr &&
    prev.display === next.display &&
    prev.angleMode === next.angleMode &&
    prev.secondMode === next.secondMode &&
    prev.fe === next.fe &&
    JSON.stringify(prev.memory) === JSON.stringify(next.memory) &&
    prev.graphExpr === next.graphExpr &&
    JSON.stringify(prev.graphSeries) === JSON.stringify(next.graphSeries) &&
    JSON.stringify(prev.graphPoints) === JSON.stringify(next.graphPoints) &&
    prev.convertCategory === next.convertCategory &&
    prev.convertFrom === next.convertFrom &&
    prev.convertTo === next.convertTo &&
    prev.convertValue === next.convertValue &&
    JSON.stringify(prev.history) === JSON.stringify(next.history) &&
    JSON.stringify(prev.graphRange) === JSON.stringify(next.graphRange);
  if (same) return Promise.resolve();
  return this.commitObjectPatch(objectId, patch, "calc-" + action.type + "-" + Date.now());
};

WbBoardScene.prototype.addCalculatorAt = function (x, y, opts) {
  if (!this.canEdit) return Promise.resolve();
  var variant = opts && opts.variant === "graph_units" ? "graph_units" : "calc";
  var size = variant === "graph_units" ? GRAPH_UNITS_DEFAULT_SIZE : CALCULATOR_DEFAULT_SIZE;
  var w = size.w;
  var h = size.h;
  var s = CALCULATOR_DEFAULT_STATE;
  var v = CALCULATOR_VARIANT[variant];
  var object = {
    id: crypto.randomUUID(),
    type: "calculator",
    x: Math.round(x - w / 2),
    y: Math.round(y - h / 2),
    w: w,
    h: h,
    mode: v.mode,
    enabledModes: v.enabledModes.slice(),
    expr: s.expr,
    display: s.display,
    history: s.history.slice(),
    angleMode: s.angleMode,
    secondMode: false,
    graphExpr: s.graphExpr,
    graphRange: Object.assign({}, s.graphRange),
    graphSeries: s.graphSeries.map(function (row) {
      return Object.assign({}, row);
    }),
    graphPoints: s.graphPoints.map(function (row) {
      return Object.assign({}, row);
    }),
    convertCategory: s.convertCategory,
    convertFrom: s.convertFrom,
    convertTo: s.convertTo,
    convertValue: s.convertValue,
    zIndex: nextZIndex(this.getObjects()),
  };
  applyFrameContext(object, frameAtPoint(this.getObjects(), x, y));
  var self = this;
  return this.addObject(object, "calculator-" + object.id).then(function () {
    // Графики: без автовыделения — двигать только через «Правка»; сразу режим курсора.
    if (variant === "graph_units") {
      self.setSelection([]);
    } else {
      self.setSelection([object.id]);
    }
    self.setActiveTool("select", true);
    return object;
  });
};

WbBoardScene.prototype.applyCodeAction = function (objectId, action) {
  var obj = this.findObject(objectId);
  if (!obj || obj.type !== "code") return Promise.resolve();
  if (!this.canEdit) return Promise.resolve();
  var prev = readCodeState(obj);
  var next = applyCodeAction(prev, action);
  // Только изменившиеся поля: правка соседнего поля другим участником
  // тогда не считается конфликтом на сервере.
  var patch = codeStatePatch(prev, next);
  if (!Object.keys(patch).length) return Promise.resolve();

  var textEdit =
    action.type === "setSource" ||
    action.type === "setHtml" ||
    action.type === "setCss" ||
    action.type === "setJs";
  if (action.type === "setLanguage") {
    clearCodeWidgetTyping(objectId);
  }
  var skipRender = textEdit && isCodeWidgetTyping(objectId);
  if (skipRender) this._suppressCodeTypingRender = true;
  var self = this;
  try {
    return this.commitObjectPatch(objectId, patch, "code-" + action.type + "-" + Date.now());
  } finally {
    if (skipRender) {
      // сброс после синхронного subscribe внутри applyLocalScene
      self._suppressCodeTypingRender = false;
    }
  }
};

WbBoardScene.prototype.addCodeAt = function (x, y) {
  if (!this.canEdit) return Promise.resolve();
  var w = CODE_DEFAULT_SIZE.w;
  var h = CODE_DEFAULT_SIZE.h;
  var d = CODE_DEFAULTS;
  var object = {
    id: crypto.randomUUID(),
    type: "code",
    x: Math.round(x - w / 2),
    y: Math.round(y - h / 2),
    w: w,
    h: h,
    language: d.language,
    source: d.source,
    html: d.html,
    css: d.css,
    js: d.js,
    stdout: d.stdout,
    zIndex: nextZIndex(this.getObjects()),
  };
  applyFrameContext(object, frameAtPoint(this.getObjects(), x, y));
  var self = this;
  return this.addObject(object, "code-" + object.id).then(function () {
    self.setSelection([object.id]);
    return object;
  });
};

WbBoardScene.prototype.applySqlAction = function (objectId, action) {
  var obj = this.findObject(objectId);
  if (!obj || obj.type !== "sql") return Promise.resolve();
  if (!this.canEdit) return Promise.resolve();
  var prev = readSqlState(obj);
  var next = applySqlAction(prev, action);
  var full = sqlStateToPatch(next);
  var keys = Object.keys(full);
  // Шлём только изменившиеся поля: иначе на каждую букву уезжал бы весь
  // результат запроса (до 24 КБ) и текст базы.
  var patch = {};
  var changed = 0;
  for (var i = 0; i < keys.length; i++) {
    var k = keys[i];
    var a = prev[k];
    var b = next[k];
    var differs;
    if (Array.isArray(a) && Array.isArray(b)) {
      differs = a.length !== b.length || a.join("") !== b.join("");
    } else {
      differs = a !== b;
    }
    if (differs) {
      patch[k] = full[k];
      changed += 1;
    }
  }
  if (!changed) return Promise.resolve();

  var textEdit =
    action.type === "setQuery" ||
    action.type === "setSetupSql" ||
    action.type === "setCustomPrompt" ||
    action.type === "setCustomSolution";
  if (!textEdit) clearSqlWidgetTyping(objectId);
  // Пока печатают — своя карточка не пересобирается, иначе теряется курсор.
  var skipRender = textEdit && isSqlWidgetTyping(objectId);
  if (skipRender) this._suppressCodeTypingRender = true;
  var self = this;
  try {
    return this.commitObjectPatch(objectId, patch, "sql-" + action.type + "-" + Date.now());
  } finally {
    if (skipRender) {
      self._suppressCodeTypingRender = false;
    }
  }
};

WbBoardScene.prototype.addSqlAt = function (x, y) {
  if (!this.canEdit) return Promise.resolve();
  var w = SQL_DEFAULT_SIZE.w;
  var h = SQL_DEFAULT_SIZE.h;
  var d = SQL_DEFAULTS;
  var object = {
    id: crypto.randomUUID(),
    type: "sql",
    x: Math.round(x - w / 2),
    y: Math.round(y - h / 2),
    w: w,
    h: h,
    dataset: d.dataset,
    mode: d.mode,
    setupSql: d.setupSql,
    query: d.query,
    taskIndex: 0,
    customPrompt: d.customPrompt,
    customSolution: d.customSolution,
    status: "idle",
    output: "",
    solvedIds: [],
    showSchema: true,
    zIndex: nextZIndex(this.getObjects()),
  };
  applyFrameContext(object, frameAtPoint(this.getObjects(), x, y));
  var self = this;
  return this.addObject(object, "sql-" + object.id).then(function () {
    self.setSelection([object.id]);
    return object;
  });
};

WbBoardScene.prototype.uploadPdfFile = function (file, x, y, onProgress) {
  if (!this.canEdit) return Promise.reject(new Error("Read-only"));
  var self = this;
  var placedId = null;
  return importPresentationToBoard({
    file: file,
    boardId: this.boardId,
    shareToken: this.shareToken,
    guestEditKey: this.guestEditKey,
    useCredentials: this.useCredentials,
    worldX: x,
    worldY: y,
    onProgress: onProgress,
    onObjectReady: function (object) {
      placedId = object.id;
      applyFrameContext(object, frameAtPoint(self.getObjects(), x, y));
      return self.addObject(object, "pdf-" + object.id).then(function () {
        self.setSelection([object.id]);
      });
    },
    onPageAssetsUpdate: function (objectId, pageAssetIds) {
      if (!self.findObject(objectId)) return Promise.resolve();
      return self.commitObjectPatch(
        objectId,
        { pageAssetIds: pageAssetIds.slice() },
        "pdf-pages-" + Date.now()
      );
    },
    onPagePreviewReady: function () {
      self.render();
      return Promise.resolve();
    },
  })
    .then(function (object) {
      if (
        placedId &&
        object.id === placedId &&
        object.pageAssetIds &&
        object.pageAssetIds.length > 1
      ) {
        var cur = self.findObject(placedId);
        var curIds = cur && cur.pageAssetIds ? cur.pageAssetIds : [];
        if (curIds.length !== object.pageAssetIds.length) {
          return self
            .commitObjectPatch(
              placedId,
              { pageAssetIds: object.pageAssetIds.slice() },
              "pdf-pages-final-" + Date.now()
            )
            .then(function () {
              return object;
            });
        }
      }
      return object;
    })
    .catch(function (err) {
      self.onError(err.message || "Не удалось импортировать презентацию");
      throw err;
    });
};

WbBoardScene.prototype.applyAudioAction = function (objectId, action) {
  if (!this.canEdit) return Promise.resolve();
  var obj = this.findObject(objectId);
  if (!obj || obj.type !== "audio") return Promise.resolve();
  if (!action || typeof action !== "object") return Promise.resolve();

  if (action.type === "setListenTogether") {
    var on = Boolean(action.listenTogether);
    if (Boolean(obj.listenTogether) === on) return Promise.resolve();
    var patch = { listenTogether: on };
    if (!on) {
      patch.audioPlaying = false;
      patch.audioStartedAt = null;
    }
    return this.commitObjectPatch(objectId, patch, "audio-together-" + Date.now());
  }

  if (action.type === "playback") {
    if (!obj.listenTogether) return Promise.resolve();
    var playing = Boolean(action.playing);
    var position = typeof action.position === "number" && Number.isFinite(action.position)
      ? Math.max(0, action.position)
      : 0;
    var same =
      Boolean(obj.audioPlaying) === playing &&
      Math.abs((typeof obj.audioPosition === "number" ? obj.audioPosition : 0) - position) < 0.2 &&
      (playing ? typeof obj.audioStartedAt === "number" : obj.audioStartedAt == null);
    if (same) return Promise.resolve();
    return this.commitBatchPatches(
      [
        {
          id: objectId,
          patch: {
            audioPlaying: playing,
            audioPosition: position,
            audioStartedAt: playing ? Date.now() : null,
            audioLeaderId: this.getSessionPeerId() || null,
          },
        },
      ],
      "audio-play-" + Date.now()
    );
  }

  return Promise.resolve();
};

WbBoardScene.prototype.uploadAudioFile = function (file, x, y) {
  if (!this.canEdit) return Promise.reject(new Error("Read-only"));
  var self = this;
  return importAudioToBoard({
    file: file,
    boardId: this.boardId,
    shareToken: this.shareToken,
    guestEditKey: this.guestEditKey,
    useCredentials: this.useCredentials,
    worldX: x,
    worldY: y,
  })
    .then(function (object) {
      applyFrameContext(object, frameAtPoint(self.getObjects(), x, y));
      return self.addObject(object, "audio-" + object.id).then(function () {
        self.setSelection([object.id]);
        return object;
      });
    })
    .catch(function (err) {
      self.onError(err.message || "Не удалось загрузить аудио");
      throw err;
    });
};

WbBoardScene.prototype.stageCenterPoint = function () {
  var viewportEl = this.stage.closest(".wb-board-viewport");
  if (!viewportEl) return { x: 0, y: 0 };
  var rect = viewportEl.getBoundingClientRect();
  return stagePoint(this, rect.left + rect.width / 2, rect.top + rect.height / 2);
};

/** Точка вставки: текущий курсор на доске, иначе центр viewport. */
WbBoardScene.prototype.pasteAnchorPoint = function () {
  var client = this._lastPointerClient;
  if (client && Number.isFinite(client.x) && Number.isFinite(client.y)) {
    try {
      var pt = stagePoint(this, client.x, client.y);
      if (pt && Number.isFinite(pt.x) && Number.isFinite(pt.y)) {
        return { x: pt.x, y: pt.y };
      }
    } catch (_e) {
      /* ignore */
    }
  }
  return this.stageCenterPoint();
};

WbBoardScene.prototype.findObject = function (id) {
  var objects = this.store.getScene().objects;
  for (var i = 0; i < objects.length; i++) {
    if (objects[i].id === id) return objects[i];
  }
  return null;
};

WbBoardScene.prototype._coerceRevision = function (value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    var n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/** Подтверждение своих ops: только revision, без замены локальной сцены (optimistic уже применён). */
WbBoardScene.prototype._applyOwnOpsAck = function (payload, clientOpId) {
  var rev = this._coerceRevision(payload && payload.revision);
  if (rev === null) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[WbBoardScene] ops ack without revision", clientOpId, payload);
    }
    return;
  }
  var prev = this.store.getRevision();
  this.store.confirmRevision(rev);
  if (typeof console !== "undefined" && console.info && rev > prev) {
    console.info(
      "[bezpart-board] ops saved",
      clientOpId || "",
      "rev",
      prev,
      "→",
      rev
    );
  }
};

WbBoardScene.prototype.applySnapshot = function (snap, opts) {
  var rev = this._coerceRevision(snap.revision);
  if (!opts || !opts.remote) {
    if (rev !== null && rev < this.store.getRevision()) {
      return;
    }
  }
  var scene = normalizeScene(snap.scene);
  if (opts && opts.remote) {
    scene = normalizeScene(this._mergeEarlyRemoteIntoScene(scene));
  }
  // Свои ops, ещё не вошедшие в снапшот, иначе исчезли бы до ack.
  scene = normalizeScene(this._reapplyPendingOps(scene));
  this.store.setRemoteSnapshot(rev ?? this.store.getRevision(), scene, {
    // Полный snapshot тоже не должен сбрасывать локальный undo (совместное рисование).
    clearUndo: false,
  });
};

WbBoardScene.prototype.reloadFromServer = function () {
  var self = this;
  return this._fetchServerSnapshot()
    .then(function (data) {
      self.applySnapshot(data.snapshot, { remote: true });
      return data;
    })
    .catch(function (err) {
      self._reportOpsError(err, "reload");
      throw err;
    });
};

WbBoardScene.prototype._reportOpsError = function (err, context) {
  var msg = formatBoardOpsErrorToast(err, context);
  this.onError(msg);
};

WbBoardScene.prototype._fetchServerSnapshot = function () {
  var self = this;
  var opts = this.fetchOpts();
  return fetch(this.stateUrl(), opts).then(function (res) {
    return parseFetchJson(res).then(function (data) {
      if (!res.ok) throw new Error(data.error || "reload failed");
      return data;
    });
  });
};

WbBoardScene.prototype._addOpsNotOnServer = function (serverSnap, ops) {
  var ids = {};
  var objs = (serverSnap && serverSnap.scene && serverSnap.scene.objects) || [];
  for (var i = 0; i < objs.length; i++) {
    ids[objs[i].id] = true;
  }
  return ops.filter(function (op) {
    if (!op || op.type !== "add_object" || !op.object || !op.object.id) return true;
    return !ids[op.object.id];
  });
};

WbBoardScene.prototype._mergeServerSnapshotForPendingAdds = function (serverSnap, ops) {
  var serverObjs = (serverSnap && serverSnap.scene && serverSnap.scene.objects) || [];
  var byId = {};
  for (var i = 0; i < serverObjs.length; i++) {
    byId[serverObjs[i].id] = true;
  }
  var extras = [];
  for (var j = 0; j < ops.length; j++) {
    var op = ops[j];
    if (!op || op.type !== "add_object" || !op.object || !op.object.id) continue;
    if (byId[op.object.id]) continue;
    var local = this.findObject(op.object.id);
    extras.push(local || op.object);
    byId[op.object.id] = true;
  }
  var merged = {
    version: (serverSnap.scene && serverSnap.scene.version) || 2,
    objects: serverObjs.concat(extras),
  };
  // Параллельные правки (другой штрих, сдвиг) не входят в ops этого ретрая.
  merged = this._reapplyPendingOps(merged);
  this.store.setRemoteSnapshot(serverSnap.revision, merged, { clearUndo: false });
};

WbBoardScene.prototype._resyncForAddConflict = function (err, ops) {
  var self = this;
  return this._fetchServerSnapshot().then(function (data) {
    var snap = data.snapshot;
    if (!snap) throw new Error("empty snapshot");
    var rev =
      typeof err.currentRevision === "number" && err.currentRevision >= 0
        ? err.currentRevision
        : snap.revision;
    if (typeof snap.revision === "number" && snap.revision > rev) {
      rev = snap.revision;
    }
    snap = { revision: rev, scene: snap.scene };
    self._mergeServerSnapshotForPendingAdds(snap, ops);
    noteDiagOps({
      ok: true,
      code: "ops_resync",
      message: "merged server rev " + rev,
      ops: ops,
    });
    return snap;
  });
};

WbBoardScene.prototype.handleOpsFailure = function (err) {
  var self = this;
  if (err && err.code === "revision_conflict") {
    return this.reloadFromServer().then(function () {
      self._reportOpsError(err, "revision_conflict");
    });
  }
  self._reportOpsError(err, "ops_failure");
  return Promise.reject(err);
};

WbBoardScene.prototype.postOpsHttp = function (ops, clientOpId) {
  var self = this;
  var body = {
    baseRevision: this.store.getRevision(),
    ops: ops,
  };
  if (clientOpId) body.clientOpId = clientOpId;

  var opts = this.fetchOpts();
  opts.method = "POST";
  opts.headers = { "Content-Type": "application/json" };
  opts.body = JSON.stringify(body);

  return fetch(this.opsUrl(), opts).then(function (res) {
    return parseFetchJson(res).then(function (data) {
      if (res.status === 409) {
        var conflict = new Error("Revision conflict");
        conflict.code = "revision_conflict";
        conflict.currentRevision = data.currentRevision;
        noteDiagOps({
          ok: false,
          code: "revision_conflict",
          message: conflict.message,
          httpStatus: 409,
          clientOpId: clientOpId,
          ops: ops,
        });
        throw conflict;
      }
      if (!res.ok) {
        var fail = new Error(
          data.message || data.error || data.code || "ops failed"
        );
        fail.code = data.code;
        if (data.code === "op_rejected" || data.error === "Op rejected") {
          fail.code = data.code || "op_rejected";
          fail.message =
            data.message ||
            (data.code ? "Сервер отклонил изменение (" + data.code + ")" : "Сервер отклонил изменение");
        }
        if (data.code === "invalid_input" || data.error === "Invalid input") {
          fail.code = "invalid_input";
          fail.message =
            data.message ||
            "Некорректные данные изменения (слишком много объектов за раз или неверный формат)";
        }
        if (res.status === 503 && data.code === "schema_outdated") {
          fail.message = "База данных на сервере устарела — выполните миграции API";
        }
        noteDiagOps({
          ok: false,
          code: fail.code || data.code || "ops_failed",
          message: fail.message,
          httpStatus: res.status,
          clientOpId: clientOpId,
          ops: ops,
        });
        throw fail;
      }
      noteDiagOps({
        ok: true,
        code: "ops_ok",
        httpStatus: res.status,
        clientOpId: clientOpId,
        ops: ops,
      });
      self._applyOwnOpsAck({ revision: data.snapshot.revision }, clientOpId);
      return data;
    });
  });
};

/**
 * Пока ops летят на сервер, они уже применены локально (optimistic), но ещё не
 * входят в подтверждённую ревизию. Любой прилетевший снапшот их бы стёр —
 * держим реестр и переприменяем поверх. Снимаем сразу после ack/ошибки.
 */
WbBoardScene.prototype._trackPendingOps = function (ops) {
  if (!ops || !ops.length) return null;
  if (!this._pendingOps) this._pendingOps = [];
  var entry = { ops: ops };
  this._pendingOps.push(entry);
  return entry;
};

WbBoardScene.prototype._untrackPendingOps = function (entry) {
  if (!entry || !this._pendingOps) return;
  var i = this._pendingOps.indexOf(entry);
  if (i >= 0) this._pendingOps.splice(i, 1);
};

/** Ops в порядке отправки — переприменяются поверх серверной сцены. */
WbBoardScene.prototype._pendingOpsFlat = function () {
  var list = this._pendingOps || [];
  var flat = [];
  for (var i = 0; i < list.length; i++) {
    flat = flat.concat(list[i].ops);
  }
  return flat;
};

/**
 * Дождаться, пока все правки уйдут на сервер. Нужен «Завершить сессию»:
 * без него последний штрих, ещё не подтверждённый сервером, мог потеряться
 * при уходе в кабинет. true — всё сохранено, false — не успели за timeoutMs.
 */
WbBoardScene.prototype.whenSaved = function (timeoutMs) {
  var self = this;
  var limit = typeof timeoutMs === "number" && timeoutMs > 0 ? timeoutMs : 8000;
  var started = Date.now();
  return new Promise(function (resolve) {
    (function check() {
      if (!self._pendingOps || self._pendingOps.length === 0) {
        resolve(true);
        return;
      }
      if (Date.now() - started >= limit) {
        resolve(false);
        return;
      }
      setTimeout(check, 120);
    })();
  });
};

WbBoardScene.prototype._reapplyPendingOps = function (scene) {
  var pending = this._pendingOpsFlat();
  if (!pending.length) return scene;
  var res = applyLocalOpsLenient(scene, pending);
  if (!res.applied) return scene;
  return res.scene;
};

WbBoardScene.prototype.postOps = function (ops, clientOpId) {
  var self = this;
  var pendingEntry = this._trackPendingOps(ops);
  return this._opsQueue
    .enqueue(function () {
      return self._dispatchOps(ops, clientOpId);
    })
    .then(
      function (res) {
        self._untrackPendingOps(pendingEntry);
        return res;
      },
      function (err) {
        self._untrackPendingOps(pendingEntry);
        throw err;
      }
    );
};

/**
 * Сервер режет body ops max(100) (boardOpsSchema) — большие пакеты шлём частями.
 * Без этого перенос/удаление выделения >100 объектов падает с invalid_input
 * и откатывается целиком («группа не двигается»).
 * onChunk(committedCount) — чтобы вызывающий знал о частичном коммите.
 */
WbBoardScene.prototype._postOpsChunked = function (ops, clientOpId, onChunk) {
  var self = this;
  if (!ops || !ops.length) return Promise.resolve();
  if (ops.length <= OPS_CHUNK_SIZE) {
    return this.postOps(ops, clientOpId).then(function (res) {
      if (typeof onChunk === "function") onChunk(ops.length);
      return res;
    });
  }
  // clientOpId обязан быть разным на чанк: сервер дедуплицирует по нему
  // и второй чанк с тем же id вернулся бы как idempotent, не применившись.
  var baseId = clientOpId || "ops-" + Date.now();
  function postChunk(start) {
    if (start >= ops.length) return Promise.resolve();
    var slice = ops.slice(start, start + OPS_CHUNK_SIZE);
    return self.postOps(slice, baseId + "-" + start).then(function () {
      if (typeof onChunk === "function") onChunk(Math.min(start + OPS_CHUNK_SIZE, ops.length));
      return postChunk(start + OPS_CHUNK_SIZE);
    });
  }
  return postChunk(0);
};

WbBoardScene.prototype._postOpsOnce = function (ops, clientOpId) {
  var self = this;
  if (this.realtime && this.realtime.canSendOps()) {
    return this.realtime
      .sendOps({
        baseRevision: this.store.getRevision(),
        ops: ops,
        clientOpId: clientOpId,
      })
      .then(function (ack) {
        noteDiagOps({
          ok: true,
          code: "ops_ok_ws",
          clientOpId: clientOpId,
          ops: ops,
        });
        self._applyOwnOpsAck(ack, clientOpId);
        return ack;
      })
      .catch(function (err) {
        noteDiagOps({
          ok: false,
          code: (err && err.code) || "ops_ws_fail",
          message: err && err.message ? String(err.message) : "ws ops fail",
          clientOpId: clientOpId,
          ops: ops,
        });
        // HTTP fallback без onError здесь — иначе дубль тоста с addObject/batch.
        return self.postOpsHttp(ops, clientOpId);
      });
  }

  return this.postOpsHttp(ops, clientOpId);
};

WbBoardScene.prototype._dispatchOps = function (ops, clientOpId) {
  var self = this;
  var pendingOps = ops;

  function attemptSend(attempt) {
    return self._postOpsOnce(pendingOps, clientOpId).catch(function (err) {
      if (isRetryableRevisionConflict(err, pendingOps) && attempt < OPS_MAX_CONFLICT_ATTEMPTS) {
        noteDiagOps({
          ok: false,
          code: "ops_retry_conflict",
          message: "attempt " + attempt,
          clientOpId: clientOpId,
          ops: pendingOps,
        });
        return self._resyncForAddConflict(err, pendingOps).then(function (serverSnap) {
          var stillNeeded = self._addOpsNotOnServer(serverSnap, pendingOps);
          if (!stillNeeded.length) {
            noteDiagOps({
              ok: true,
              code: "ops_skip_present",
              clientOpId: clientOpId,
              ops: pendingOps,
            });
            return { revision: self.store.getRevision(), skipped: true };
          }
          pendingOps = stillNeeded;
          return attemptSend(attempt + 1);
        });
      }
      if (isRetryableLiveTextConflict(err, pendingOps) && attempt < OPS_MAX_CONFLICT_ATTEMPTS) {
        noteDiagOps({
          ok: false,
          code: "ops_retry_live_text",
          message: "attempt " + attempt,
          clientOpId: clientOpId,
          ops: pendingOps,
        });
        // Снимок с сервера + наши неотправленные правки поверх, затем та же
        // правка с новой ревизией: последний напечатавший и остаётся на экране у всех.
        return self._fetchServerSnapshot().then(function (data) {
          self.applySnapshot(data.snapshot, { remote: true });
          return attemptSend(attempt + 1);
        });
      }
      if (isTransientOpsError(err) && attempt < OPS_MAX_TRANSIENT_ATTEMPTS) {
        noteDiagOps({
          ok: false,
          code: "ops_retry_transient",
          message: (err && err.message ? String(err.message) : "transient") + " attempt " + attempt,
          clientOpId: clientOpId,
          ops: pendingOps,
        });
        return delayMs(opsRetryDelayMs(attempt)).then(function () {
          return attemptSend(attempt + 1);
        });
      }
      throw err;
    });
  }

  return attemptSend(1);
};

export { WbBoardScene };
